import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { formatBrazilPhone, formatCpfCnpj, isValidBrazilPhone, isValidCpfCnpj } from "@/lib/inputFormatters";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Loader2, Save, MapPin, Bot, Sparkles, MessageCircle, Smartphone, CheckCircle2, AlertCircle, RefreshCw, ExternalLink, Upload, X, ImageIcon, Store, Copy, QrCode, Clock, PauseCircle, PlayCircle, Cpu, Key, ChevronDown, FileText, Shield, Download, Building2, CalendarX, Plus, Trash2, CalendarClock, Mail, Send, CheckCircle, XCircle, CreditCard, Banknote, Link2, Palette, Type, Share2, Eye, Users, Scissors } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { MapView } from "@/components/Map";
import { QRCodeSVG } from "qrcode.react";

// ─── Componente de Histórico de Previsões do Tempo ─────────────────────────────
function WeatherLogsCard() {
  const [page, setPage] = useState(1);
  const { data, isLoading, refetch } = (trpc as any).weather.getLogs.useQuery({ page, pageSize: 10 }, { keepPreviousData: true });
  const logs: any[] = data?.logs ?? [];
  const total: number = data?.total ?? 0;
  const totalPages = Math.ceil(total / 10);

  function uvBadgeColor(uv: number) {
    if (uv >= 10) return "bg-red-500/20 text-red-400 border-red-500/30";
    if (uv >= 8) return "bg-orange-500/20 text-orange-400 border-orange-500/30";
    if (uv >= 6) return "bg-yellow-500/20 text-yellow-500 border-yellow-500/30";
    if (uv >= 5) return "bg-green-500/20 text-green-400 border-green-500/30";
    return "bg-muted text-muted-foreground border-border";
  }

  return (
    <Card className="border-border/50 border-sky-500/20 bg-sky-500/5">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="text-base">🌤️</span>
            Histórico de Previsões do Tempo
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => refetch()} className="h-7 w-7 p-0">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>
        <CardDescription>
          Registro de todas as consultas de clima feitas pelo agente de IA para aprovação de agendamentos de bronze.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground text-sm">
            <span className="text-2xl block mb-2">🌤️</span>
            Nenhuma consulta de clima registrada ainda.
            <br />
            <span className="text-xs">As consultas aparecem aqui quando o agente verifica o tempo para agendamentos de bronze.</span>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map((log: any) => (
              <div key={log.id} className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border border-border/40 text-sm">
                <div className="mt-0.5">
                  {log.isSuitable ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <XCircle className="w-4 h-4 text-red-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-foreground">{log.city}</span>
                    <span className="text-muted-foreground">{log.date}</span>
                    <Badge variant="outline" className={`text-xs px-1.5 py-0 ${uvBadgeColor(log.maxUvIndex)}`}>
                      UV {log.maxUvIndex}
                    </Badge>
                    <Badge variant="outline" className={`text-xs px-1.5 py-0 ${
                      log.decision === 'approved' ? 'bg-green-500/10 text-green-500 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'
                    }`}>
                      {log.decision === 'approved' ? 'Aprovado' : 'Recusado'}
                    </Badge>
                  </div>
                  {log.bestPeriod && (
                    <p className="text-xs text-muted-foreground mt-0.5">Melhor período: {log.bestPeriod}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {new Date(log.createdAt).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
                  </p>
                </div>
              </div>
            ))}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                  Anterior
                </Button>
                <span className="text-xs text-muted-foreground">{page} / {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                  Próxima
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function AdminSettings() {
  const utils = trpc.useUtils();
  const { data: salon, isLoading } = trpc.salon.getMySalon.useQuery();
  const [form, setForm] = useState({
    name: "",
    address: "",
    city: "",
    phone: "",
    email: "",
    description: "",
    latitude: "",
    longitude: "",
    maxTanningHour: 11,
    uvMinIndex: 5,
  });

  const [contractForm, setContractForm] = useState({ cnpj: "", legalResponsible: "" });

  const [agentForm, setAgentForm] = useState({
    agentName: "",
    agentPersonality: "",
    agentWelcomeMessage: "",
    servedAudience: "all" as "all" | "female" | "male",
    humanResumeEmoji: "🤖",
    humanModeTimeoutMinutes: 120,
    agentReplyDelaySeconds: 0,
    agentEnableWeather: true,
    tanningArrivalIntentsEnabled: false,
    postServiceMessage: "",
    supervisorPhone1: "",
    supervisorPhone2: "",
  });
  const [agentFormDirty, setAgentFormDirty] = useState(false);

  const [llmForm, setLlmForm] = useState({
    aiProvider: "manus" as "manus" | "openai" | "deepseek" | "groq" | "gemini",
    aiModel: "",
    aiApiKey: "",
  });

  const [metaForm, setMetaForm] = useState({
    metaInstagramToken: "",
    metaInstagramIgId: "",
    metaAppSecret: "",
  });

  const [siteForm, setSiteForm] = useState({
    heroSlogan: "",
    heroSubtitle: "",
    ctaButtonText: "Agendar agora",
    storeCtaButtonText: "",
    storeCtaButtonUrl: "",
    publicStoreDomain: "",
    themeColor: "#C97D2E",
    themeBgColor: "#F5F0E8",
    themeDarkColor: "#2C1810",
    instagramUrl: "",
    facebookUrl: "",
    tiktokUrl: "",
    whatsappUrl: "",
    showTeamSection: true,
    showReviewsSection: true,
    showHoursSection: true,
  });
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [bannerUploading, setBannerUploading] = useState(false);

  useEffect(() => {
    if (salon) {
      setForm({
        name: salon.name ?? "",
        address: salon.address ?? "",
        city: (salon as any).city ?? "",
        phone: salon.phone ?? "",
        email: salon.email ?? "",
        description: salon.description ?? "",
        latitude: salon.latitude ? String(salon.latitude) : "",
        longitude: salon.longitude ? String(salon.longitude) : "",
        maxTanningHour: (salon as any).maxTanningHour ?? 11,
        uvMinIndex: (salon as any).uvMinIndex ?? 5,
      });
      setContractForm({
        cnpj: (salon as any).cnpj ?? "",
        legalResponsible: (salon as any).legalResponsible ?? "",
      });
      if (!agentFormDirty) {
        setAgentForm({
          agentName: (salon as any).agentName ?? "Assistente",
          agentPersonality: (salon as any).agentPersonality ?? "",
          agentWelcomeMessage: (salon as any).agentWelcomeMessage ?? "",
          servedAudience: (salon as any).servedAudience ?? "all",
          humanResumeEmoji: (salon as any).humanResumeEmoji ?? "🤖",
          humanModeTimeoutMinutes: (salon as any).humanModeTimeoutMinutes ?? 120,
          agentReplyDelaySeconds: (salon as any).agentReplyDelaySeconds ?? 0,
          agentEnableWeather: (salon as any).agentEnableWeather !== false,
          tanningArrivalIntentsEnabled: (salon as any).tanningArrivalIntentsEnabled === true,
          postServiceMessage: (salon as any).postServiceMessage ?? "",
          supervisorPhone1: (salon as any).supervisorPhone1 ?? "",
          supervisorPhone2: (salon as any).supervisorPhone2 ?? "",
        });
      }
      setLlmForm({
        aiProvider: ((salon as any).aiProvider ?? "manus") as "manus" | "openai" | "deepseek" | "groq" | "gemini",
        aiModel: (salon as any).aiModel ?? "",
        aiApiKey: (salon as any).aiApiKey ?? "",
      });
      setMetaForm({
        metaInstagramToken: (salon as any).metaInstagramToken ?? "",
        metaInstagramIgId: (salon as any).metaInstagramIgId ?? "",
        metaAppSecret: (salon as any).metaAppSecret ?? "",
      });
      setSiteForm({
        heroSlogan: (salon as any).heroSlogan ?? "",
        heroSubtitle: (salon as any).heroSubtitle ?? "",
        ctaButtonText: (salon as any).ctaButtonText ?? "Agendar agora",
        storeCtaButtonText: (salon as any).storeCtaButtonText ?? "",
        storeCtaButtonUrl: (salon as any).storeCtaButtonUrl ?? "",
        publicStoreDomain: (salon as any).publicStoreDomain ?? "",
        themeColor: (salon as any).themeColor ?? "#C97D2E",
        themeBgColor: (salon as any).themeBgColor ?? "#F5F0E8",
        themeDarkColor: (salon as any).themeDarkColor ?? "#2C1810",
        instagramUrl: (salon as any).instagramUrl ?? "",
        facebookUrl: (salon as any).facebookUrl ?? "",
        tiktokUrl: (salon as any).tiktokUrl ?? "",
        whatsappUrl: (salon as any).whatsappUrl ?? "",
        showTeamSection: (salon as any).showTeamSection !== false,
        showReviewsSection: (salon as any).showReviewsSection !== false,
        showHoursSection: (salon as any).showHoursSection !== false,
      });
    }
  }, [salon, agentFormDirty]);

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoUploading, setLogoUploading] = useState(false);

  const updateMutation = trpc.salon.update.useMutation({
    onSuccess: () => { toast.success("Configurações salvas!"); utils.salon.getMySalon.invalidate(); },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const verifyStoreCustomDomainMutation = (trpc as any).salon.verifyStoreCustomDomain.useMutation({
    onSuccess: (result: { verified: boolean }) => {
      utils.salon.getMySalon.invalidate();
      if (result.verified) {
        toast.success("Domínio verificado. Conecte-o à hospedagem do projeto antes de divulgar o novo endereço.");
      } else {
        toast.error("Registro DNS ainda não encontrado. Confira o tipo TXT, o nome e o código e tente novamente em alguns minutos.");
      }
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const uploadLogoMutation = trpc.salon.uploadLogo.useMutation({
    onSuccess: (data: any) => {
      toast.success("Logo atualizada com sucesso!");
      setLogoPreview(null);
      utils.salon.getMySalon.invalidate();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const uploadBannerMutation = trpc.salon.uploadBanner.useMutation({
    onSuccess: () => {
      toast.success("Banner atualizado!");
      setBannerPreview(null);
      utils.salon.getMySalon.invalidate();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const handleSaveSite = async () => {
    if (bannerPreview) {
      setBannerUploading(true);
      try {
        const mimeMatch = bannerPreview.match(/^data:([^;]+);/);
        const mimeType = mimeMatch?.[1] ?? "image/jpeg";
        await uploadBannerMutation.mutateAsync({ base64: bannerPreview, mimeType });
      } finally {
        setBannerUploading(false);
      }
    }
    await updateMutation.mutateAsync(siteForm as any);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("A imagem deve ter no máximo 5MB.");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLogoPreview(ev.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoPreview) return;
    setLogoUploading(true);
    try {
      const mimeMatch = logoPreview.match(/^data:([^;]+);/);
      const mimeType = mimeMatch?.[1] ?? "image/jpeg";
      await uploadLogoMutation.mutateAsync({ base64: logoPreview, mimeType });
    } finally {
      setLogoUploading(false);
    }
  };

  const { data: channelRequests } = trpc.salon.getChannelRequests.useQuery();

  // ─── Horário de Funcionamento ────────────────────────────────────────────────
  const DAY_NAMES_FULL = ["Domingo", "Segunda-feira", "Terça-feira", "Quarta-feira", "Quinta-feira", "Sexta-feira", "Sábado"];
  const { data: workingScheduleData, isLoading: scheduleLoading } = (trpc as any).salon.getWorkingSchedule.useQuery();
  const [schedule, setSchedule] = useState<Array<{ dayOfWeek: number; isOpen: boolean; openTime: string; closeTime: string }>>(
    Array.from({ length: 7 }, (_, i) => ({
      dayOfWeek: i,
      isOpen: i >= 1 && i <= 5,
      openTime: "08:00",
      closeTime: i >= 1 && i <= 5 ? "18:00" : i === 6 ? "13:00" : "18:00",
    }))
  );

  useEffect(() => {
    if (workingScheduleData && workingScheduleData.length > 0) {
      setSchedule(workingScheduleData.map((d: any) => ({
        dayOfWeek: d.dayOfWeek,
        isOpen: d.isOpen,
        openTime: d.openTime ?? "08:00",
        closeTime: d.closeTime ?? "18:00",
      })));
    }
  }, [workingScheduleData]);

  const setScheduleMutation = (trpc as any).salon.setWorkingSchedule.useMutation({
    onSuccess: () => {
      toast.success("Horário de funcionamento salvo!");
      (utils.salon as any).getWorkingSchedule.invalidate();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const handleSaveSchedule = () => {
    setScheduleMutation.mutate({
      schedule: schedule.map((d: any) => ({
        dayOfWeek: d.dayOfWeek,
        isOpen: d.isOpen,
        openTime: d.isOpen ? d.openTime : null,
        closeTime: d.isOpen ? d.closeTime : null,
      })),
    });
  };

  const copyHoursToOpenDays = (sourceDay: { dayOfWeek: number; openTime: string; closeTime: string }) => {
    setSchedule(current => current.map(day => (
      day.isOpen
        ? { ...day, openTime: sourceDay.openTime, closeTime: sourceDay.closeTime }
        : day
    )));
    toast.success(`Horário de ${DAY_NAMES_FULL[sourceDay.dayOfWeek]} aplicado aos dias abertos.`);
  };

  // ─── Exceções de Horário (Feriados / Datas Especiais) ─────────────────────────────────
  const { data: exceptionsData, refetch: refetchExceptions } = (trpc as any).salon.listScheduleExceptions.useQuery();
  const [showExceptionForm, setShowExceptionForm] = useState(false);
  const [exceptionForm, setExceptionForm] = useState({
    exceptionDate: "",
    isClosed: true,
    openTime: "",
    closeTime: "",
    reason: "",
  });

  const upsertExceptionMutation = (trpc as any).salon.upsertScheduleException.useMutation({
    onSuccess: () => {
      toast.success("Exceção de horário salva!");
      refetchExceptions();
      setShowExceptionForm(false);
      setExceptionForm({ exceptionDate: "", isClosed: true, openTime: "", closeTime: "", reason: "" });
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const deleteExceptionMutation = (trpc as any).salon.deleteScheduleException.useMutation({
    onSuccess: () => {
      toast.success("Exceção removida.");
      refetchExceptions();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const handleSaveException = () => {
    if (!exceptionForm.exceptionDate) {
      toast.error("Informe a data da exceção.");
      return;
    }
    upsertExceptionMutation.mutate({
      exceptionDate: exceptionForm.exceptionDate,
      isClosed: exceptionForm.isClosed,
      openTime: exceptionForm.isClosed ? null : (exceptionForm.openTime || null),
      closeTime: exceptionForm.isClosed ? null : (exceptionForm.closeTime || null),
      reason: exceptionForm.reason || null,
    });
  };

  // ─── Pagamento Antecipado ─────────────────────────────────────────────────────────────────────────────────
  const { data: paymentConfig, refetch: refetchPaymentConfig } = (trpc as any).salon.getPaymentConfig.useQuery();
  const [paymentForm, setPaymentForm] = useState({
    requireAdvancePayment: false,
    advancePaymentPercent: 100,
    publicBookingCancellationHours: 24,
    publicBookingCancellationPolicy: "",
    pixKey: "",
    pixKeyType: "" as "" | "cpf" | "cnpj" | "email" | "telefone" | "aleatoria",
    paymentLink: "",
    cardDebitFeePercent: "",
    cardCreditFeePercent: "",
    cardCreditInstallmentFeePercents: Object.fromEntries(Array.from({ length: 11 }, (_, index) => [String(index + 2), ""])) as Record<string, string>,
  });

  useEffect(() => {
    if (paymentConfig) {
      setPaymentForm({
        requireAdvancePayment: paymentConfig.requireAdvancePayment ?? false,
        advancePaymentPercent: paymentConfig.advancePaymentPercent ?? 100,
        publicBookingCancellationHours: paymentConfig.publicBookingCancellationHours ?? 24,
        publicBookingCancellationPolicy: paymentConfig.publicBookingCancellationPolicy ?? "",
        pixKey: paymentConfig.pixKey ?? "",
        pixKeyType: (paymentConfig.pixKeyType ?? "") as any,
        paymentLink: paymentConfig.paymentLink ?? "",
        cardDebitFeePercent: paymentConfig.cardDebitFeePercent?.toString() ?? "",
        cardCreditFeePercent: paymentConfig.cardCreditFeePercent?.toString() ?? "",
        cardCreditInstallmentFeePercents: {
          ...Object.fromEntries(Array.from({ length: 11 }, (_, index) => [String(index + 2), ""])),
          ...(() => {
            try { return paymentConfig.cardCreditInstallmentFeePercents ? JSON.parse(paymentConfig.cardCreditInstallmentFeePercents) : {}; }
            catch { return {}; }
          })(),
        },
      });
    }
  }, [paymentConfig]);

  const updatePaymentConfigMutation = (trpc as any).salon.updatePaymentConfig.useMutation({
    onSuccess: () => {
      toast.success("Configuração de pagamento salva!");
      refetchPaymentConfig();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const handleSavePaymentConfig = () => {
    const parseOptionalFee = (value: string) => value.trim() === "" ? null : Number(value.replace(",", "."));
    const debitFee = parseOptionalFee(paymentForm.cardDebitFeePercent);
    const creditFee = parseOptionalFee(paymentForm.cardCreditFeePercent);
    const installmentFees = Object.fromEntries(
      Object.entries(paymentForm.cardCreditInstallmentFeePercents)
        .map(([installments, fee]) => [installments, parseOptionalFee(fee)] as const)
        .filter((entry): entry is [string, number] => entry[1] !== null),
    );
    const configuredFees = [debitFee, creditFee, ...Object.values(installmentFees)];
    if (configuredFees.some((fee) => fee !== null && (!Number.isFinite(fee) || fee < 0 || fee > 100))) {
      toast.error("Cada taxa deve estar entre 0% e 100%.");
      return;
    }
    updatePaymentConfigMutation.mutate({
      requireAdvancePayment: paymentForm.requireAdvancePayment,
      advancePaymentPercent: paymentForm.advancePaymentPercent,
      publicBookingCancellationHours: paymentForm.publicBookingCancellationHours,
      publicBookingCancellationPolicy: paymentForm.publicBookingCancellationPolicy,
      pixKey: paymentForm.pixKey || undefined,
      pixKeyType: (paymentForm.pixKeyType || undefined) as any,
      paymentLink: paymentForm.paymentLink || undefined,
      cardDebitFeePercent: debitFee,
      cardCreditFeePercent: creditFee,
      cardCreditInstallmentFeePercents: installmentFees,
    });
  };

  // ─── Configuração SMTP ─────────────────────────────────────────────────────────────────────────────────
  const { data: smtpConfig, refetch: refetchSmtpConfig } = (trpc as any).salon.getSmtpConfig.useQuery();
  const [smtpForm, setSmtpForm] = useState({
    smtpHost: "",
    smtpPort: 587,
    smtpUser: "",
    smtpPass: "",
    smtpFrom: "",
  });
  const [smtpTestResult, setSmtpTestResult] = useState<{ ok: boolean; error?: string } | null>(null);

  useEffect(() => {
    if (smtpConfig) {
      setSmtpForm({
        smtpHost: smtpConfig.smtpHost ?? "",
        smtpPort: smtpConfig.smtpPort ?? 587,
        smtpUser: smtpConfig.smtpUser ?? "",
        smtpPass: "", // nunca pré-preencher senha
        smtpFrom: smtpConfig.smtpFrom ?? "",
      });
    }
  }, [smtpConfig]);

  const setSmtpConfigMutation = (trpc as any).salon.setSmtpConfig.useMutation({
    onSuccess: () => {
      toast.success("Configuração SMTP salva!");
      refetchSmtpConfig();
      setSmtpForm((f) => ({ ...f, smtpPass: "" }));
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const testSmtpMutation = (trpc as any).salon.testSmtpConfig.useMutation({
    onSuccess: (result: { ok: boolean; error?: string }) => {
      setSmtpTestResult(result);
      if (result.ok) toast.success("Conexão SMTP verificada com sucesso!");
      else toast.error(`Falha na conexão SMTP: ${result.error}`);
    },
    onError: (e: { message: string }) => {
      setSmtpTestResult({ ok: false, error: e.message });
      toast.error(e.message);
    },
  });

  const handleSaveSmtp = () => {
    setSmtpConfigMutation.mutate({
      smtpHost: smtpForm.smtpHost,
      smtpPort: smtpForm.smtpPort,
      smtpUser: smtpForm.smtpUser,
      smtpPass: smtpForm.smtpPass || undefined,
      smtpFrom: smtpForm.smtpFrom || undefined,
    });
  };

  const handleTestSmtp = () => {
    if (!smtpForm.smtpHost || !smtpForm.smtpUser) {
      toast.error("Preencha pelo menos o servidor e o usuário SMTP.");
      return;
    }
    if (!smtpForm.smtpPass && !smtpConfig?.smtpPassSet) {
      toast.error("Informe a senha SMTP para testar.");
      return;
    }
    setSmtpTestResult(null);
    testSmtpMutation.mutate({
      smtpHost: smtpForm.smtpHost,
      smtpPort: smtpForm.smtpPort,
      smtpUser: smtpForm.smtpUser,
      smtpPass: smtpForm.smtpPass || "__USE_SAVED__",
    });
  };

  const [channelForm, setChannelForm] = useState({
    channelType: "whatsapp" as "whatsapp" | "instagram",
    contactPhone: "",
    contactName: "",
    notes: "",
  });
  const [showChannelForm, setShowChannelForm] = useState<"whatsapp" | "instagram" | null>(null);

  // ─── Instagram Direct via Evolution API ─────────────────────────────────────────
  const [showInstagramSetup, setShowInstagramSetup] = useState(false);
  const [instagramInstanceName, setInstagramInstanceName] = useState("");
  const [instagramApiKey, setInstagramApiKey] = useState("");
  const [showQR, setShowQR] = useState(false);

  const { data: instagramConfig, refetch: refetchInstagramConfig } = (trpc as any).salon.getInstagramConfig.useQuery();
  const { data: instagramStatus, refetch: refetchInstagramStatus } = (trpc as any).salon.getInstagramStatus.useQuery(
    undefined,
    { refetchInterval: showQR ? 5000 : false } // polling a cada 5s enquanto aguarda QR
  );
  const { data: instagramQR, refetch: refetchQR } = (trpc as any).salon.getInstagramQR.useQuery(
    undefined,
    { enabled: showQR }
  );

  const createInstagramInstanceMutation = (trpc as any).salon.createInstagramInstance.useMutation({
    onSuccess: (data: any) => {
      toast.success(`Instância "${data.instanceName}" criada! Agora conecte sua conta Instagram.`);
      setShowQR(true);
      refetchInstagramConfig();
      refetchQR();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const setInstagramConfigMutation = (trpc as any).salon.setInstagramConfig.useMutation({
    onSuccess: () => {
      toast.success("Configuração Instagram salva!");
      refetchInstagramConfig();
      refetchInstagramStatus();
      setShowInstagramSetup(false);
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const disconnectInstagramMutation = (trpc as any).salon.disconnectInstagram.useMutation({
    onSuccess: () => {
      toast.success("Instagram desconectado.");
      setShowQR(false);
      refetchInstagramConfig();
      refetchInstagramStatus();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  // Fechar QR quando conectado
  useEffect(() => {
    if (instagramStatus?.connected && showQR) {
      setShowQR(false);
      toast.success("Instagram conectado com sucesso!");
    }
  }, [instagramStatus?.connected]);

  const requestChannelMutation = trpc.salon.requestChannelConnection.useMutation({
    onSuccess: () => {
      toast.success("Solicitação enviada! Nossa equipe entrará em contato em breve.");
      setShowChannelForm(null);
      utils.salon.getChannelRequests.invalidate();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const handleSave = () => {
    // Validar e-mail se preenchido
    const emailTrimmed = form.email?.trim() ?? "";
    const formattedPhone = formatBrazilPhone(form.phone ?? "");
    const formattedDocument = formatCpfCnpj(contractForm.cnpj ?? "");
    if (emailTrimmed !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(emailTrimmed)) {
        toast.error("E-mail inválido. Verifique o formato (ex: contato@salao.com)");
        return;
      }
    }
    if (formattedPhone && !isValidBrazilPhone(formattedPhone)) {
      toast.error("Informe um telefone válido com DDD.");
      return;
    }
    if (formattedDocument && !isValidCpfCnpj(formattedDocument)) {
      toast.error("Informe um CNPJ ou CPF válido para o contrato.");
      return;
    }
    updateMutation.mutate({ ...form, phone: formattedPhone, ...contractForm, cnpj: formattedDocument, email: emailTrimmed || undefined });
  };
  const handleSaveAgent = () => {
    updateMutation.mutate(agentForm, {
      onSuccess: () => setAgentFormDirty(false),
    });
  };
  const handleSaveLlm = () => updateMutation.mutate(llmForm);

  // ─── Pausar / Retomar Agente ────────────────────────────────────────────────
  const [pauseReason, setPauseReason] = useState("");
  const { data: agentStatus, refetch: refetchAgentStatus } = (trpc as any).salon.getAgentStatus.useQuery();

  const pauseAgentMutation = (trpc as any).salon.pauseAgent.useMutation({
    onSuccess: () => {
      toast.success("Agente pausado. Mensagens não serão respondidas até você retomar.");
      refetchAgentStatus();
      setPauseReason("");
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const resumeAgentMutation = (trpc as any).salon.resumeAgent.useMutation({
    onSuccess: () => {
      toast.success("Agente retomado! Voltando a responder mensagens.");
      refetchAgentStatus();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const agentPersonalityPlaceholder = `Você é ${agentForm.agentName || "Assistente"}, assistente virtual do ${form.name || "salão"}. Seja simpática, profissional e prestativa. Ajude clientes a agendar serviços, verificar disponibilidade e tirar dúvidas sobre preços e horários.

Quando o cliente quiser agendar, colete: nome completo, serviço desejado, data e horário preferido. Confirme sempre antes de finalizar o agendamento.

Não invente informações. Se não souber algo, diga que vai verificar com a equipe.`;

  const publicStoreDomain = (salon as any)?.publicStoreDomain?.trim() ?? "";
  const publicStoreDomainStatus = (salon as any)?.publicStoreDomainStatus ?? "disabled";
  const genericPublicStoreUrl = salon?.slug ? `${window.location.origin}/s/${salon.slug}/loja` : "";
  const publicStoreUrl = publicStoreDomainStatus === "verified" && publicStoreDomain
    ? `https://${publicStoreDomain}`
    : genericPublicStoreUrl;
  const publicStoreLaunchUrl = publicStoreDomainStatus === "verified" && publicStoreDomain
    ? publicStoreUrl
    : `/s/${salon?.slug}/loja`;
  const publicStoreVerificationHost = publicStoreDomain ? `_belezabronze-verification.${publicStoreDomain}` : "";
  const publicStoreVerificationValue = (salon as any)?.publicStoreDomainVerificationToken
    ? `belezabronze-store-domain=${(salon as any).publicStoreDomainVerificationToken}`
    : "";

  const handleSettingsSectionNavigation = (event: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    event.preventDefault();
    const section = document.getElementById(sectionId);
    if (!section) return;

    window.history.replaceState(null, "", `#${sectionId}`);
    section.scrollIntoView({ behavior: "smooth", block: "start" });
    section.focus({ preventScroll: true });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-[Cormorant_Garamond,serif] font-semibold">Configurações do Salão</h1>
        <p className="text-sm text-muted-foreground mt-0.5">Comece pelo essencial e acesse configurações específicas quando precisar.</p>
      </div>

      <Card className="border-primary/20 bg-primary/5 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Ir para uma seção</CardTitle>
          <CardDescription>Escolha o que deseja configurar agora.</CardDescription>
        </CardHeader>
        <CardContent>
          <nav aria-label="Seções das configurações do salão" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {[
              { id: "settings-essential", label: "Essencial", description: "Dados e links" },
              { id: "settings-hours", label: "Horários", description: "Funcionamento e exceções" },
              { id: "settings-team-services", label: "Equipe e serviços", description: "Cadastros do salão" },
              { id: "settings-ai", label: "IA e automações", description: "Assistente e respostas" },
              { id: "settings-store-channels", label: "Loja e canais", description: "Atendimento e vitrine" },
              { id: "settings-advanced", label: "Avançado", description: "Pagamento, e-mail e segurança" },
            ].map((section) => (
              <a
                key={section.id}
                href={`#${section.id}`}
                onClick={(event) => handleSettingsSectionNavigation(event, section.id)}
                className="flex min-h-20 flex-col justify-center rounded-xl border border-border/70 bg-card px-3 py-2.5 text-card-foreground shadow-sm transition-colors hover:border-primary/50 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
              >
                <span className="text-sm font-semibold leading-tight">{section.label}</span>
                <span className="mt-1 text-xs leading-tight text-muted-foreground">{section.description}</span>
              </a>
            ))}
          </nav>
        </CardContent>
      </Card>

      <div className="scroll-mt-4 border-t border-border/50 pt-4 sm:scroll-mt-6" aria-labelledby="settings-essential">
        <h2 id="settings-essential" tabIndex={-1} className="text-xl font-[Cormorant_Garamond,serif] font-semibold outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
          Essencial
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground">Atualize os dados do salão, localização e links que seus clientes usam.</p>
      </div>

      {/* Links Públicos + QR Codes */}
      {salon?.slug && (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            {/* Link da Página Pública */}
            <Card className="border-amber-500/30 bg-amber-50/5">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium">Página pública do salão</p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {window.location.origin}/s/{salon.slug}
                    </p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/s/${salon.slug}`); toast.success("Link copiado!"); }}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <a href={`/s/${salon.slug}`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm"><ExternalLink className="w-3.5 h-3.5 mr-1" />Ver</Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
            {/* Link da Loja Pública */}
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium flex items-center gap-1.5">
                      <Store className="w-3.5 h-3.5 text-primary" />
                      Loja de produtos
                    </p>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">
                      {publicStoreUrl}
                    </p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(publicStoreUrl); toast.success("Link da loja copiado!"); }}>
                      <Copy className="w-3.5 h-3.5" />
                    </Button>
                    <a href={publicStoreLaunchUrl} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm"><ExternalLink className="w-3.5 h-3.5 mr-1" />Ver</Button>
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
          {/* QR Codes */}
          <Card className="border-border/50">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <QrCode className="w-4 h-4 text-primary" />
                QR Codes para compartilhamento
              </CardTitle>
              <CardDescription className="text-xs">Imprima ou compartilhe para que clientes acessem rapidamente via celular.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6 sm:grid-cols-2">
                <div className="flex flex-col items-center gap-3">
                  <p className="text-sm font-medium">Página do Salão</p>
                  <div className="p-3 bg-white rounded-xl border border-border/40 shadow-sm">
                    <QRCodeSVG value={`${window.location.origin}/s/${salon.slug}`} size={160} level="M" fgColor="#000000" bgColor="#ffffff" />
                  </div>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/s/${salon.slug}`); toast.success("Link copiado!"); }}>
                    <Copy className="w-3.5 h-3.5" />Copiar link
                  </Button>
                </div>
                <div className="flex flex-col items-center gap-3">
                  <p className="text-sm font-medium">Loja de Produtos</p>
                  <div className="p-3 bg-white rounded-xl border border-border/40 shadow-sm">
                    <QRCodeSVG value={publicStoreUrl} size={160} level="M" fgColor="#000000" bgColor="#ffffff" />
                  </div>
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { navigator.clipboard.writeText(publicStoreUrl); toast.success("Link da loja copiado!"); }}>
                    <Copy className="w-3.5 h-3.5" />Copiar link
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Informações Gerais */}
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Informações Gerais</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* Upload de Logo */}
          <div>
            <Label className="text-sm">Logo do Salão</Label>
            <p className="text-xs text-muted-foreground mt-0.5 mb-2">Aparece no Painel de Chamada e na página pública. Máx. 5MB (JPG, PNG, WebP).</p>
            <div className="flex items-start gap-4">
              {/* Preview atual ou nova */}
              <div className="w-20 h-20 rounded-xl border-2 border-dashed border-border/50 flex items-center justify-center overflow-hidden bg-secondary/20 shrink-0">
                {logoPreview ? (
                  <img src={logoPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : salon?.logoUrl ? (
                  <img src={salon.logoUrl} alt="Logo atual" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-8 h-8 text-muted-foreground/40" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label htmlFor="logo-upload" className="cursor-pointer">
                  <Button variant="outline" size="sm" className="gap-1.5" asChild>
                    <span>
                      <Upload className="w-3.5 h-3.5" />
                      {salon?.logoUrl ? "Trocar logo" : "Enviar logo"}
                    </span>
                  </Button>
                  <input
                    id="logo-upload"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif"
                    className="hidden"
                    onChange={handleLogoChange}
                  />
                </label>
                {logoPreview && (
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      className="gap-1.5 bg-primary text-primary-foreground h-8"
                      onClick={handleLogoUpload}
                      disabled={logoUploading}
                    >
                      {logoUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      Salvar logo
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-8 w-8 p-0 text-muted-foreground"
                      onClick={() => setLogoPreview(null)}
                    >
                      <X className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                )}
                {salon?.logoUrl && !logoPreview && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-destructive border-destructive/30 hover:bg-destructive/10 gap-1"
                    onClick={() => updateMutation.mutate({ logoUrl: "" })}
                  >
                    <X className="w-3 h-3" />
                    Remover logo
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-sm">Nome do Salão</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" />
          </div>
          <div>
            <Label className="text-sm">Descrição</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 resize-none"
              rows={3}
              placeholder="Descreva seu salão..."
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Telefone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: formatBrazilPhone(e.target.value) })} inputMode="tel" className="mt-1" placeholder="(11) 99999-9999" />
            </div>
            <div>
              <Label className="text-sm">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" placeholder="contato@salao.com" />
            </div>
          </div>
          <div>
            <Label className="text-sm">Endereço</Label>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1" placeholder="Rua, número, bairro" />
          </div>
          <div>
            <Label className="text-sm">Cidade</Label>
            <Input
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
              className="mt-1"
              placeholder="Ex: Feira de Santana"
            />
            <p className="text-xs text-muted-foreground mt-1">Usada pelo agente de IA para verificar a previsão do tempo corretamente.</p>
          </div>
          {/* Dados para contrato */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">CNPJ / CPF</Label>
              <Input
                value={contractForm.cnpj}
                onChange={(e) => setContractForm({ ...contractForm, cnpj: formatCpfCnpj(e.target.value) })}
                inputMode="numeric"
                className="mt-1"
                placeholder="00.000.000/0001-00"
              />
              <p className="text-xs text-muted-foreground mt-1">Preenchido automaticamente no contrato SaaS.</p>
            </div>
            <div>
              <Label className="text-sm">Responsável Legal</Label>
              <Input
                value={contractForm.legalResponsible}
                onChange={(e) => setContractForm({ ...contractForm, legalResponsible: e.target.value })}
                className="mt-1"
                placeholder="Nome completo"
              />
              <p className="text-xs text-muted-foreground mt-1">Assina o contrato.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Localização */}
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MapPin className="w-4 h-4 text-primary" />
            Localização no Mapa
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-sm">Latitude</Label>
              <Input
                value={form.latitude}
                onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                className="mt-1"
                placeholder="-23.5505"
              />
            </div>
            <div>
              <Label className="text-sm">Longitude</Label>
              <Input
                value={form.longitude}
                onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                className="mt-1"
                placeholder="-46.6333"
              />
            </div>
          </div>
          {form.latitude && form.longitude ? (
            <div className="rounded-lg overflow-hidden border border-border/50 h-48">
              <MapView
                onMapReady={(map) => {
                  const lat = parseFloat(form.latitude);
                  const lng = parseFloat(form.longitude);
                  if (!isNaN(lat) && !isNaN(lng)) {
                    map.setCenter({ lat, lng });
                    map.setZoom(15);
                    new google.maps.Marker({
                      position: { lat, lng },
                      map,
                      title: form.name,
                    });
                  }
                }}
              />
            </div>
          ) : (
            <div className="rounded-lg border border-border/50 h-48 flex items-center justify-center bg-secondary/20">
              <p className="text-sm text-muted-foreground">Insira latitude e longitude para visualizar o mapa</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Button
        onClick={handleSave}
        disabled={updateMutation.isPending}
        className="bg-primary text-primary-foreground w-full sm:w-auto"
      >
        {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        Salvar Configurações
      </Button>

      <div className="scroll-mt-4 border-t border-border/50 pt-4 sm:scroll-mt-6" aria-labelledby="settings-team-services">
        <h2 id="settings-team-services" tabIndex={-1} className="text-xl font-[Cormorant_Garamond,serif] font-semibold outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
          Equipe e serviços
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground">Cadastre os serviços oferecidos e quem pode realizá-los.</p>
      </div>

      <Card className="border-border/50 bg-secondary/10">
        <CardContent className="grid gap-3 pt-4 sm:grid-cols-2">
          <a
            href="/services"
            aria-label="Abrir cadastro de serviços"
            className="flex min-h-24 items-center gap-3 rounded-xl border border-border/60 bg-card p-4 text-card-foreground shadow-sm transition-colors hover:border-primary/50 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <Scissors className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <span>
              <span className="block text-sm font-semibold">Serviços</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">Preços, duração e opções disponíveis.</span>
            </span>
            <ExternalLink className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </a>
          <a
            href="/professionals"
            aria-label="Abrir cadastro de profissionais"
            className="flex min-h-24 items-center gap-3 rounded-xl border border-border/60 bg-card p-4 text-card-foreground shadow-sm transition-colors hover:border-primary/50 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            <Users className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
            <span>
              <span className="block text-sm font-semibold">Profissionais</span>
              <span className="mt-0.5 block text-xs text-muted-foreground">Equipe, especialidades e disponibilidade.</span>
            </span>
            <ExternalLink className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          </a>
        </CardContent>
      </Card>

      {/* Separador */}
      <div className="scroll-mt-4 border-t border-border/50 pt-4 sm:scroll-mt-6" aria-labelledby="settings-ai">
        <div className="flex items-center gap-2 mb-1">
          <Bot className="w-5 h-5 text-primary" />
          <h2 id="settings-ai" tabIndex={-1} className="text-xl font-[Cormorant_Garamond,serif] font-semibold outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">IA e automações</h2>
          <Badge variant="secondary" className="text-xs gap-1">
            <Sparkles className="w-3 h-3" />
            Powered by Zaia
          </Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Personalize o assistente e as respostas automáticas para seus clientes.
        </p>
      </div>

      {/* Card de Pausar / Retomar Agente */}
      <Card className={`border-2 ${agentStatus?.paused ? 'border-amber-500/60 bg-amber-500/5' : 'border-emerald-500/40 bg-emerald-500/5'}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              {agentStatus?.paused
                ? <PauseCircle className="w-4 h-4 text-amber-500" />
                : <PlayCircle className="w-4 h-4 text-emerald-500" />}
              Status do Agente
            </CardTitle>
            <Badge variant={agentStatus?.paused ? 'outline' : 'default'}
              className={agentStatus?.paused ? 'border-amber-500 text-amber-600' : 'bg-emerald-500 text-white'}>
              {agentStatus?.paused ? 'Pausado' : 'Ativo'}
            </Badge>
          </div>
          <CardDescription>
            {agentStatus?.paused
              ? `Agente pausado${agentStatus.pausedReason ? ` — ${agentStatus.pausedReason}` : ''}. Mensagens recebidas não serão respondidas.`
              : 'O agente está ativo e respondendo mensagens normalmente.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {agentStatus?.paused ? (
            <div className="flex flex-col gap-3">
              {agentStatus.pausedAt && (
                <p className="text-xs text-muted-foreground">
                  Pausado em: {new Date(agentStatus.pausedAt).toLocaleString('pt-BR')}
                </p>
              )}
              <Button
                onClick={() => resumeAgentMutation.mutate()}
                disabled={resumeAgentMutation.isPending}
                className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
              >
                {resumeAgentMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PlayCircle className="w-4 h-4 mr-2" />}
                Retomar Agente
              </Button>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <div>
                <Label className="text-sm">Motivo da pausa (opcional)</Label>
                <Input
                  value={pauseReason}
                  onChange={(e) => setPauseReason(e.target.value)}
                  className="mt-1"
                  placeholder="Ex: Atendimento manual, manutensão, feriado..."
                  maxLength={120}
                />
              </div>
              <Button
                variant="outline"
                onClick={() => pauseAgentMutation.mutate({ reason: pauseReason || undefined })}
                disabled={pauseAgentMutation.isPending}
                className="w-full border-amber-500/60 text-amber-600 hover:bg-amber-500/10"
              >
                {pauseAgentMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PauseCircle className="w-4 h-4 mr-2" />}
                Pausar Agente
              </Button>
              <p className="text-xs text-muted-foreground">
                O WhatsApp continua conectado. Apenas as respostas automáticas serão suspensas.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Configurações do Agente */}
      <Card className="border-border/50 border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Bot className="w-4 h-4 text-primary" />
            Identidade do Assistente
          </CardTitle>
          <CardDescription>
            Defina o nome e a personalidade do seu assistente virtual
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm">Nome do Assistente</Label>
            <Input
              value={agentForm.agentName}
              onChange={(e) => setAgentForm({ ...agentForm, agentName: e.target.value })}
              className="mt-1"
              placeholder="Ex: Bella, Sofia, Luna..."
              maxLength={64}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Este nome aparece no chat e é usado pelo agente para se identificar.
            </p>
          </div>

          <div>
            <Label className="text-sm flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              Personalidade e Instruções
            </Label>
            <Textarea
              value={agentForm.agentPersonality}
              onChange={(e) => setAgentForm({ ...agentForm, agentPersonality: e.target.value })}
              className="mt-1 resize-none font-mono text-xs"
              rows={8}
              placeholder={agentPersonalityPlaceholder}
              maxLength={2000}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Descreva como o assistente deve se comportar. Se deixar em branco, usará o comportamento padrão.
              Limite: 2000 caracteres ({agentForm.agentPersonality.length}/2000).
            </p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/50 border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            Público Atendido
          </CardTitle>
          <CardDescription>
            Informe ao agente se esta unidade atende público feminino, masculino ou ambos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label className="text-sm font-medium" htmlFor="served-audience">Política de atendimento</Label>
          <Select value={agentForm.servedAudience} onValueChange={(value: "all" | "female" | "male") => setAgentForm({ ...agentForm, servedAudience: value })}>
            <SelectTrigger id="served-audience" className="w-full">
              <SelectValue placeholder="Selecione o público atendido" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Público feminino e masculino</SelectItem>
              <SelectItem value="female">Somente público feminino</SelectItem>
              <SelectItem value="male">Somente público masculino</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            O agente informa a regra com gentileza quando a pessoa perguntar. Ele não supõe gênero pelo nome.
          </p>
        </CardContent>
      </Card>

      <Card className="border-border/50 border-primary/20 bg-primary/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-primary" />
            Mensagem de Boas-vindas
          </CardTitle>
          <CardDescription>
            Primeira mensagem que o assistente envia ao iniciar uma conversa
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={agentForm.agentWelcomeMessage}
            onChange={(e) => setAgentForm({ ...agentForm, agentWelcomeMessage: e.target.value })}
            className="resize-none"
            rows={3}
            placeholder={`Olá! Sou ${agentForm.agentName || "a assistente"} do ${form.name || "salão"}. Como posso te ajudar hoje? 😊`}
            maxLength={500}
          />
          <p className="text-xs text-muted-foreground mt-1">
            Limite: 500 caracteres ({agentForm.agentWelcomeMessage.length}/500).
          </p>
        </CardContent>
      </Card>

      {/* Modo Humano */}
      <Card className="border-border/50 border-amber-500/20 bg-amber-500/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="text-base">👤</span>
            Modo Humano (Pausa do Agente)
          </CardTitle>
          <CardDescription>
            Quando você responder manualmente no WhatsApp, o agente pausa automaticamente para aquele contato. Para retomar, envie o emoji abaixo na conversa.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label className="text-sm font-medium">Emoji de Retomada</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Envie este emoji no WhatsApp para o agente voltar a responder aquele contato.</p>
            </div>
            <Input
              type="text"
              value={agentForm.humanResumeEmoji}
              onChange={(e) => setAgentForm({ ...agentForm, humanResumeEmoji: e.target.value })}
              className="w-20 text-center text-2xl"
              maxLength={4}
              placeholder="🤖"
            />
          </div>
          {/* Timeout automático */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label className="text-sm font-medium">Retomada Automática</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Após este tempo sem resposta humana, o agente retoma automaticamente. Use 0 para nunca retomar.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                max={10080}
                value={agentForm.humanModeTimeoutMinutes}
                onChange={(e) => setAgentForm({ ...agentForm, humanModeTimeoutMinutes: Math.max(0, parseInt(e.target.value) || 0) })}
                className="w-20 text-center"
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap">min</span>
            </div>
          </div>
          <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Como funciona:</p>
            <p>1. Agente está respondendo um cliente no WhatsApp</p>
            <p>2. Você responde manualmente nessa conversa → agente pausa automaticamente</p>
            <p>3. Para o agente retomar, envie <strong className="text-foreground">{agentForm.humanResumeEmoji || "🤖"}</strong> nessa mesma conversa</p>
            <p>4. O agente volta a responder automaticamente</p>
            {agentForm.humanModeTimeoutMinutes > 0 && (
              <p className="text-amber-400">5. Ou aguarde {agentForm.humanModeTimeoutMinutes} minutos — o agente retoma sozinho</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delay de Resposta do Agente */}
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <span>⏱️</span> Delay de Resposta
          </CardTitle>
          <p className="text-xs text-muted-foreground">Aguarda alguns segundos antes de enviar a resposta, tornando a interação mais natural.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Delay antes de responder</Label>
              <p className="text-xs text-muted-foreground mt-0.5">0 = resposta imediata · máximo 7 segundos</p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={0}
                max={7}
                value={agentForm.agentReplyDelaySeconds}
                onChange={(e) => setAgentForm({ ...agentForm, agentReplyDelaySeconds: Math.min(7, Math.max(0, parseInt(e.target.value) || 0)) })}
                className="w-20 text-center"
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap">seg</span>
            </div>
          </div>
          <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Dica:</p>
            <p>Um delay de 2–4 segundos simula o tempo de digitação humana e reduz a sensação de resposta robótica.</p>
            {agentForm.agentReplyDelaySeconds === 0 && <p className="text-amber-400">Atualmente: resposta imediata (sem delay)</p>}
            {agentForm.agentReplyDelaySeconds > 0 && <p className="text-green-400">Atualmente: aguarda {agentForm.agentReplyDelaySeconds}s antes de responder</p>}
          </div>
        </CardContent>
      </Card>

      {/* Previsão do Tempo */}
      <Card className="border-border/50 border-sky-500/20 bg-sky-500/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="text-base">⛅</span>
            Previsão do Tempo
          </CardTitle>
          <CardDescription>
            Quando ativado, o agente consulta a previsão do tempo antes de confirmar agendamentos de bronzeamento solar. Desative se não trabalhar com bronze ao sol.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Consultar previsão do tempo</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                {agentForm.agentEnableWeather
                  ? "✅ Ativo — o agente verifica o clima antes de agendar bronze solar"
                  : "❌ Desativado — o agente não consultará o clima"}
              </p>
            </div>
            <Switch
              checked={agentForm.agentEnableWeather}
              onCheckedChange={(v) => setAgentForm({ ...agentForm, agentEnableWeather: v })}
            />
          </div>
                </CardContent>
      </Card>

      {/* Pós-Atendimento e Supervisão */}
      <Card className="border-border/50 border-purple-500/20 bg-purple-500/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-purple-400" />
            Pós-Atendimento e Supervisão
          </CardTitle>
          <CardDescription>
            Configure a mensagem enviada ao cliente após o atendimento e os números que receberão notificações de supervisão.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* Mensagem pós-atendimento */}
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Mensagem Pós-Atendimento</Label>
            <p className="text-xs text-muted-foreground">
              Enviada automaticamente 2 minutos após o atendimento ser concluído. Deixe em branco para usar a mensagem padrão.
            </p>
            <Textarea
              rows={3}
              maxLength={500}
              value={agentForm.postServiceMessage}
              onChange={(e) => setAgentForm({ ...agentForm, postServiceMessage: e.target.value })}
              placeholder={`Obrigada pela visita, {nome}! Esperamos que tenha adorado o {servico}. Qualquer dúvida, estamos aqui. ❤️`}
              className="resize-none"
            />
            <p className="text-xs text-muted-foreground">Variáveis disponíveis: <code>&#123;nome&#125;</code>, <code>&#123;servico&#125;</code>, <code>&#123;profissional&#125;</code>, <code>&#123;salao&#125;</code></p>
          </div>

          {/* Números de supervisão */}
          <div className="space-y-3">
            <div>
              <Label className="text-sm font-medium">Número de Supervisão 1</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Recebe notificação via WhatsApp com resumo do atendimento. Formato: 5571999999999</p>
              <Input
                className="mt-1.5"
                value={agentForm.supervisorPhone1}
                onChange={(e) => {
                  setAgentFormDirty(true);
                  setAgentForm({ ...agentForm, supervisorPhone1: e.target.value.replace(/\D/g, "") });
                }}
                placeholder="5571999999999"
                maxLength={20}
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Número de Supervisão 2 (opcional)</Label>
              <p className="text-xs text-muted-foreground mt-0.5">Segundo número que também receberá as notificações. Formato: 5571999999999</p>
              <Input
                className="mt-1.5"
                value={agentForm.supervisorPhone2}
                onChange={(e) => {
                  setAgentFormDirty(true);
                  setAgentForm({ ...agentForm, supervisorPhone2: e.target.value.replace(/\D/g, "") });
                }}
                placeholder="5571999999999"
                maxLength={20}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Bronzeamento Solar */}
      <Card className="border-border/50 border-yellow-500/20 bg-yellow-500/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <span className="text-base">☀️</span>
            Bronzeamento Solar
          </CardTitle>
          <CardDescription>
            Configure o horário máximo para sessões de bronzeamento. O agente de IA usará este limite ao verificar a previsão do tempo e aprovar agendamentos.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Label className="text-sm font-medium">Horário Máximo para Bronze</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Sessões de bronzeamento só serão aprovadas se o UV estiver alto até este horário. Padrão: 11h.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={6}
                max={23}
                value={form.maxTanningHour}
                onChange={(e) => setForm({ ...form, maxTanningHour: Math.min(23, Math.max(6, parseInt(e.target.value) || 11)) })}
                className="w-20 text-center"
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap">h</span>
            </div>
          </div>
          <div className="flex items-center gap-4 pt-2 border-t border-border/50">
            <div className="flex-1">
              <Label className="text-sm font-medium">UV Mínimo para Bronze</Label>
              <p className="text-xs text-muted-foreground mt-0.5">
                Índice UV mínimo necessário para aprovar sessões de bronzeamento. Padrão: 5 (moderado).
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min={1}
                max={11}
                value={form.uvMinIndex}
                onChange={(e) => setForm({ ...form, uvMinIndex: Math.min(11, Math.max(1, parseInt(e.target.value) || 5)) })}
                className="w-20 text-center"
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap">UV</span>
            </div>
          </div>
          <div className="rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Regras do agente para bronze:</p>
            <p>1. UV mínimo <strong className="text-foreground">{form.uvMinIndex}</strong> necessário para o bronze funcionar (configurado acima)</p>
            <p>2. Sessão matinal vai até <strong className="text-foreground">{form.maxTanningHour}h</strong> (configurado acima)</p>
            <p>3. Se de manhã tiver sol + UV ≥ {form.uvMinIndex}, aprova mesmo que à tarde chova</p>
            <p>4. Mensagem de confirmação inclui previsão de UV do dia</p>
          </div>
        </CardContent>
      </Card>

      {/* Histórico de Previsões do Tempo */}
      <WeatherLogsCard />

      {/* Provedor de LLM */}
      <Card className="border-border/50 border-amber-500/20 bg-amber-500/5">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Cpu className="w-4 h-4 text-amber-500" />
            Modelo de IA
          </CardTitle>
          <CardDescription>
            Escolha qual modelo de inteligência artificial o assistente usará. O modelo Manus é o padrão e não requer configuração adicional.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-sm">Provedor</Label>
            <Select
              value={llmForm.aiProvider}
              onValueChange={(v) => setLlmForm({ ...llmForm, aiProvider: v as typeof llmForm.aiProvider, aiModel: "", aiApiKey: "" })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Selecionar provedor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manus">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-primary inline-block" />
                    Manus IA (Padrão) — sem custo adicional
                  </span>
                </SelectItem>
                <SelectItem value="openai">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                    OpenAI (GPT-4o, GPT-4.1)
                  </span>
                </SelectItem>
                <SelectItem value="deepseek">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                    DeepSeek (V3, R1)
                  </span>
                </SelectItem>
                <SelectItem value="groq">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />
                    Groq (Llama, Mixtral) — ultra rápido
                  </span>
                </SelectItem>
                <SelectItem value="gemini">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-sky-500 inline-block" />
                    Google Gemini — gratuito
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {llmForm.aiProvider !== "manus" && (
            <>
              <div>
                <Label className="text-sm">Modelo</Label>
                <Select
                  value={llmForm.aiModel || "default"}
                  onValueChange={(v) => setLlmForm({ ...llmForm, aiModel: v === "default" ? "" : v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Modelo padrão do provedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {llmForm.aiProvider === "openai" && (
                      <>
                        <SelectItem value="gpt-4o-mini">GPT-4o Mini — rápido e econômico</SelectItem>
                        <SelectItem value="gpt-4o">GPT-4o — mais inteligente</SelectItem>
                        <SelectItem value="gpt-4.1">GPT-4.1 — última geração</SelectItem>
                        <SelectItem value="gpt-4.1-mini">GPT-4.1 Mini — econômico</SelectItem>
                      </>
                    )}
                    {llmForm.aiProvider === "deepseek" && (
                      <>
                        <SelectItem value="deepseek-chat">DeepSeek Chat (V3) — recomendado</SelectItem>
                        <SelectItem value="deepseek-reasoner">DeepSeek Reasoner (R1) — raciocínio avançado</SelectItem>
                      </>
                    )}
                    {llmForm.aiProvider === "groq" && (
                      <>
                        <SelectItem value="llama-3.3-70b-versatile">Llama 3.3 70B — muito rápido</SelectItem>
                        <SelectItem value="llama-3.1-8b-instant">Llama 3.1 8B Instant — ultra rápido</SelectItem>
                        <SelectItem value="mixtral-8x7b-32768">Mixtral 8x7B — equilibrado</SelectItem>
                      </>
                    )}
                    {llmForm.aiProvider === "gemini" && (
                      <>
                        <SelectItem value="gemini-2.0-flash">Gemini 2.0 Flash — rápido e gratuito (recomendado)</SelectItem>
                        <SelectItem value="gemini-2.0-flash-lite">Gemini 2.0 Flash Lite — ultra leve e gratuito</SelectItem>
                        <SelectItem value="gemini-1.5-flash">Gemini 1.5 Flash — versão estável gratuita</SelectItem>
                        <SelectItem value="gemini-1.5-pro">Gemini 1.5 Pro — máxima qualidade Google</SelectItem>
                      </>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Se deixar em branco, usa o modelo padrão do provedor.</p>
              </div>

              <div>
                <Label className="text-sm flex items-center gap-1.5">
                  <Key className="w-3.5 h-3.5 text-amber-500" />
                  API Key do Provedor
                </Label>
                <Input
                  type="password"
                  value={llmForm.aiApiKey}
                  onChange={(e) => setLlmForm({ ...llmForm, aiApiKey: e.target.value })}
                  className="mt-1 font-mono text-xs"
                  placeholder={`${llmForm.aiProvider === "gemini" ? "AIzaSy... (chave do Google AI Studio)" : `sk-... (chave da ${llmForm.aiProvider === "openai" ? "OpenAI" : llmForm.aiProvider === "deepseek" ? "DeepSeek" : "Groq"})`}`}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  A chave é armazenada de forma segura e nunca é exposta ao cliente.{" "}
                  {llmForm.aiProvider === "openai" && <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer" className="underline text-primary">Obter chave OpenAI →</a>}
                  {llmForm.aiProvider === "deepseek" && <a href="https://platform.deepseek.com/api_keys" target="_blank" rel="noreferrer" className="underline text-primary">Obter chave DeepSeek →</a>}
                  {llmForm.aiProvider === "groq" && <a href="https://console.groq.com/keys" target="_blank" rel="noreferrer" className="underline text-primary">Obter chave Groq →</a>}
                  {llmForm.aiProvider === "gemini" && <a href="https://aistudio.google.com/app/apikey" target="_blank" rel="noreferrer" className="underline text-primary">Obter chave Google AI Studio →</a>}
                </p>
              </div>
            </>
          )}

          {llmForm.aiProvider === "manus" && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/10 border border-primary/20">
              <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">Manus IA está ativo.</strong> Nenhuma configuração adicional necessária. O modelo interno é otimizado para atendimento em salões de beleza.
              </p>
            </div>
          )}

          <Button
            onClick={handleSaveLlm}
            disabled={updateMutation.isPending}
            size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-white"
          >
            {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar Modelo de IA
          </Button>
        </CardContent>
      </Card>

      {/* Prévia do contexto automático */}
      <Card className="border-border/50 bg-secondary/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-muted-foreground flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            Contexto injetado automaticamente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground leading-relaxed">
            O assistente recebe automaticamente: <strong>nome e dados do salão</strong>,{" "}
            <strong>lista completa de serviços com preços e duração</strong>,{" "}
            <strong>profissionais disponíveis com especialidades</strong>,{" "}
            <strong>horários de funcionamento por dia da semana</strong> e{" "}
            <strong>acesso às APIs de disponibilidade e agendamento</strong>.
            Você não precisa descrever isso nas instruções acima.
          </p>
        </CardContent>
      </Card>

      <Button
        onClick={handleSaveAgent}
        disabled={updateMutation.isPending}
        className="bg-primary text-primary-foreground"
      >
        {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
        Salvar Configurações do Assistente
      </Button>

      {/* Conexão de Canais */}
      <div className="scroll-mt-4 border-t border-border/50 pt-4 sm:scroll-mt-6" aria-labelledby="settings-store-channels">
        <div className="flex items-center gap-2 mb-1">
          <Smartphone className="w-5 h-5 text-green-500" />
          <h2 id="settings-store-channels" tabIndex={-1} className="text-xl font-[Cormorant_Garamond,serif] font-semibold outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">Loja e canais</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Conecte os canais de atendimento e, mais abaixo, personalize a loja e a presença pública.
        </p>
      </div>

      <p className="-mt-2 text-sm font-medium text-foreground">Canais de atendimento</p>

      {/* Solicitações existentes */}
      {channelRequests && channelRequests.length > 0 && (
        <Card className="border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Suas Solicitações</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {channelRequests.map((req: any) => (
              <div key={req.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/30 border border-border/50">
                <div className="flex items-center gap-2">
                  {req.channelType === "whatsapp" ? (
                    <MessageCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <Sparkles className="w-4 h-4 text-pink-500" />
                  )}
                  <div>
                    <p className="text-sm font-medium">
                      {req.channelType === "whatsapp" ? "WhatsApp" : "Instagram"}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(req.createdAt).toLocaleDateString("pt-BR")}
                    </p>
                  </div>
                </div>
                <Badge
                  className={
                    req.status === "done" ? "bg-green-500/15 text-green-600 border-green-500/30" :
                    req.status === "in_progress" ? "bg-blue-500/15 text-blue-600 border-blue-500/30" :
                    req.status === "cancelled" ? "bg-red-500/15 text-red-600 border-red-500/30" :
                    "bg-amber-500/15 text-amber-600 border-amber-500/30"
                  }
                >
                  {req.status === "done" ? "Concluído" :
                   req.status === "in_progress" ? "Em andamento" :
                   req.status === "cancelled" ? "Cancelado" : "Aguardando"}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Botões de solicitação */}
      <Card className="border-border/50">
        <CardContent className="pt-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setShowChannelForm(showChannelForm === "whatsapp" ? null : "whatsapp")}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                showChannelForm === "whatsapp"
                  ? "border-green-500 bg-green-500/10"
                  : "border-border/50 hover:border-green-500/50 hover:bg-green-500/5"
              }`}
            >
              <MessageCircle className="w-7 h-7 text-green-500" />
              <span className="text-sm font-medium">WhatsApp</span>
              <span className="text-xs text-muted-foreground text-center">Assistente responde no WhatsApp do salão</span>
            </button>
            <button
              onClick={() => setShowChannelForm(showChannelForm === "instagram" ? null : "instagram")}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${
                showChannelForm === "instagram"
                  ? "border-pink-500 bg-pink-500/10"
                  : "border-border/50 hover:border-pink-500/50 hover:bg-pink-500/5"
              }`}
            >
              <Sparkles className="w-7 h-7 text-pink-500" />
              <span className="text-sm font-medium">Instagram</span>
              <span className="text-xs text-muted-foreground text-center">Assistente responde nas DMs do Instagram</span>
            </button>
          </div>

          {/* Formulário de solicitação */}
          {showChannelForm && (
            <div className="border-t border-border/50 pt-4 space-y-3">
              <p className="text-sm font-medium">
                Solicitar conexão de {showChannelForm === "whatsapp" ? "WhatsApp" : "Instagram"}
              </p>
              <p className="text-xs text-muted-foreground">
                Preencha os dados abaixo e nossa equipe entrará em contato para configurar tudo.
              </p>
              <div>
                <Label className="text-sm">Seu nome</Label>
                <Input
                  value={channelForm.contactName}
                  onChange={(e) => setChannelForm({ ...channelForm, contactName: e.target.value })}
                  className="mt-1"
                  placeholder="Nome do responsável"
                />
              </div>
              {showChannelForm === "whatsapp" && (
                <div>
                  <Label className="text-sm">Número do WhatsApp a conectar</Label>
                  <Input
                    value={channelForm.contactPhone}
                    onChange={(e) => setChannelForm({ ...channelForm, contactPhone: e.target.value })}
                    className="mt-1"
                    placeholder="(11) 99999-9999"
                  />
                </div>
              )}
              <div>
                <Label className="text-sm">Observações (opcional)</Label>
                <Textarea
                  value={channelForm.notes}
                  onChange={(e) => setChannelForm({ ...channelForm, notes: e.target.value })}
                  className="mt-1 resize-none"
                  rows={2}
                  placeholder="Alguma informação adicional..."
                />
              </div>
              <Button
                className={`w-full ${
                  showChannelForm === "whatsapp"
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : "bg-pink-600 hover:bg-pink-700 text-white"
                }`}
                onClick={() => requestChannelMutation.mutate({
                  channelType: showChannelForm,
                  contactPhone: channelForm.contactPhone || undefined,
                  contactName: channelForm.contactName || undefined,
                  notes: channelForm.notes || undefined,
                })}
                disabled={requestChannelMutation.isPending}
              >
                {requestChannelMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <MessageCircle className="w-4 h-4 mr-2" />
                )}
                Enviar Solicitação
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Instagram Direct via Evolution API ───────────────────────────────────────────── */}
      <Card className="border-pink-500/30">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-pink-500" />
              Instagram Direct (Evolution API)
            </CardTitle>
            {instagramStatus?.connected ? (
              <Badge className="bg-green-500/15 text-green-600 border-green-500/30 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Conectado
              </Badge>
            ) : instagramConfig?.evolutionInstagramInstance ? (
              <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" /> Desconectado
              </Badge>
            ) : (
              <Badge className="bg-muted text-muted-foreground">Não configurado</Badge>
            )}
          </div>
          <CardDescription className="text-xs">
            Conecte sua conta Instagram Business para que o agente responda automaticamente nas DMs.
            Requer Evolution API v2+ configurada no WhatsApp.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {instagramConfig?.evolutionInstagramInstance ? (
            // Já tem instância configurada
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30 border border-border/50">
                <Sparkles className="w-5 h-5 text-pink-500 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">Instância: {instagramConfig.evolutionInstagramInstance}</p>
                  <p className="text-xs text-muted-foreground">
                    Estado: <span className={instagramStatus?.connected ? "text-green-600" : "text-amber-600"}>
                      {instagramStatus?.state ?? "verificando..."}
                    </span>
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { refetchInstagramStatus(); toast.info("Verificando status..."); }}
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                  </Button>
                  {!instagramStatus?.connected && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setShowQR(true); refetchQR(); }}
                    >
                      <QrCode className="w-3.5 h-3.5 mr-1" /> QR Code
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-500 hover:text-red-600 hover:bg-red-500/10"
                    onClick={() => disconnectInstagramMutation.mutate()}
                    disabled={disconnectInstagramMutation.isPending}
                  >
                    {disconnectInstagramMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              </div>

              {/* QR Code para conectar */}
              {showQR && (
                <div className="border border-pink-500/30 rounded-xl p-4 bg-pink-500/5 space-y-3">
                  <p className="text-sm font-medium text-center">Conectar conta Instagram</p>
                  <p className="text-xs text-muted-foreground text-center">
                    Abra o Instagram no celular, vá em Configurações → Segurança → Aplicativos e sites
                    e escaneie o QR Code abaixo. Ou use o código de emparelhamento.
                  </p>
                  {instagramQR?.qrcode ? (
                    <div className="flex flex-col items-center gap-3">
                      <div style={{background:'#ffffff',padding:'12px',borderRadius:'8px',display:'inline-block',border:'1px solid #e5e7eb'}}>
                        <img src={instagramQR.qrcode} alt="QR Code Instagram" className="w-64 h-64" style={{display:'block',background:'#ffffff',filter:'grayscale(1) contrast(2)'}} />
                      </div>
                      {instagramQR.pairingCode && (
                        <div className="text-center">
                          <p className="text-xs text-muted-foreground mb-1">Código de emparelhamento:</p>
                          <code className="text-lg font-mono font-bold tracking-widest text-pink-600">{instagramQR.pairingCode}</code>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className="w-6 h-6 animate-spin text-pink-500" />
                      <span className="ml-2 text-sm text-muted-foreground">Gerando QR Code...</span>
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => refetchQR()}>
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Atualizar QR
                    </Button>
                    <Button variant="outline" size="sm" className="flex-1" onClick={() => setShowQR(false)}>
                      Fechar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            // Sem instância configurada
            <div className="space-y-3">
              {!instagramConfig?.evolutionApiUrl ? (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                  <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-700">
                    Configure primeiro a Evolution API na seção de WhatsApp (URL + API Key) antes de conectar o Instagram.
                  </p>
                </div>
              ) : (
                <>
                  {!showInstagramSetup ? (
                    <Button
                      className="w-full bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700"
                      onClick={() => setShowInstagramSetup(true)}
                    >
                      <Sparkles className="w-4 h-4 mr-2" /> Configurar Instagram Direct
                    </Button>
                  ) : (
                    <div className="space-y-3 border border-pink-500/30 rounded-xl p-4 bg-pink-500/5">
                      <p className="text-sm font-medium">Nova instância Instagram</p>
                      <div>
                        <Label className="text-xs">Nome da instância</Label>
                        <Input
                          value={instagramInstanceName}
                          onChange={(e) => setInstagramInstanceName(e.target.value)}
                          placeholder="ex: meu-salao-instagram"
                          className="mt-1 h-8 text-sm"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Use apenas letras minúsculas, números e hífens.</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 bg-pink-600 hover:bg-pink-700 text-white"
                          onClick={() => createInstagramInstanceMutation.mutate({ instanceName: instagramInstanceName })}
                          disabled={!instagramInstanceName.trim() || createInstagramInstanceMutation.isPending}
                        >
                          {createInstagramInstanceMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                          Criar Instância
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setShowInstagramSetup(false)}>Cancelar</Button>
                      </div>
                      <div className="border-t border-border/50 pt-3">
                        <p className="text-xs text-muted-foreground mb-2">Ou configure manualmente se já tem uma instância Instagram:</p>
                        <div className="space-y-2">
                          <Input
                            value={instagramInstanceName}
                            onChange={(e) => setInstagramInstanceName(e.target.value)}
                            placeholder="Nome da instância existente"
                            className="h-8 text-sm"
                          />
                          <Input
                            value={instagramApiKey}
                            onChange={(e) => setInstagramApiKey(e.target.value)}
                            placeholder="API Key (deixe vazio para usar a do WhatsApp)"
                            className="h-8 text-sm"
                            type="password"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full"
                            onClick={() => setInstagramConfigMutation.mutate({
                              evolutionInstagramInstance: instagramInstanceName || null,
                              evolutionInstagramApiKey: instagramApiKey || undefined,
                            })}
                            disabled={!instagramInstanceName.trim() || setInstagramConfigMutation.isPending}
                          >
                            {setInstagramConfigMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
                            Salvar Configuração Manual
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Instagram Direct (Meta Graph API) ──────────────────────────────────────────── */}
      <Card className="border-pink-500/30">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-pink-500" />
              Instagram Direct (Meta API Oficial)
            </CardTitle>
            {metaForm.metaInstagramToken && metaForm.metaInstagramIgId ? (
              <Badge className="bg-green-500/15 text-green-600 border-green-500/30 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Configurado
              </Badge>
            ) : (
              <Badge className="bg-muted text-muted-foreground">Não configurado</Badge>
            )}
          </div>
          <CardDescription className="text-xs">
            Integração oficial via Meta Graph API. Mais estável que a Evolution API. Requer um app Meta com permissão <code>instagram_manage_messages</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-muted-foreground">Instagram Business Account ID</Label>
              <Input
                className="mt-1 font-mono text-sm"
                placeholder="Ex: 17841442040505871"
                value={metaForm.metaInstagramIgId}
                onChange={(e) => setMetaForm({ ...metaForm, metaInstagramIgId: e.target.value.trim() })}
              />
              <p className="text-xs text-muted-foreground mt-1">Encontre em: Meta for Developers → seu app → Instagram → Gerar tokens → ID da conta</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Page Access Token</Label>
              <Input
                className="mt-1 font-mono text-sm"
                type="password"
                placeholder="EAAxxxxxxxxx..."
                value={metaForm.metaInstagramToken}
                onChange={(e) => setMetaForm({ ...metaForm, metaInstagramToken: e.target.value.trim() })}
              />
              <p className="text-xs text-muted-foreground mt-1">Token gerado na aba API Setup do app Meta. Válido por 60 dias (renove periodicamente).</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">App Secret (opcional, para verificação de webhook)</Label>
              <Input
                className="mt-1 font-mono text-sm"
                type="password"
                placeholder="App Secret do app Meta"
                value={metaForm.metaAppSecret}
                onChange={(e) => setMetaForm({ ...metaForm, metaAppSecret: e.target.value.trim() })}
              />
            </div>
            <div className="p-3 rounded-lg bg-secondary/30 border border-border/50 space-y-1">
              <p className="text-xs font-medium text-foreground">URL do Webhook para configurar no Meta:</p>
              <code className="text-xs text-pink-400 break-all">
                {`${window.location.origin}/api/agent/webhook/${(salon as any)?.id ?? "SEU_SALON_ID"}`}
              </code>
              <p className="text-xs text-muted-foreground mt-1">Token de verificação: <code className="text-pink-400">{`salon-${(salon as any)?.id ?? "SEU_SALON_ID"}`}</code></p>
              <p className="text-xs text-muted-foreground">Campos a assinar: <code className="text-pink-400">messages</code></p>
            </div>
            <Button
              className="w-full bg-pink-600 hover:bg-pink-700 text-white"
              onClick={() => updateMutation.mutate(metaForm as any)}
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar Configuração Meta Instagram
            </Button>
          </div>
        </CardContent>
      </Card>
      {/* ─── Horário de Funcionamento ────────────────────────────────────────────────────── */}
      <div className="scroll-mt-4 border-t border-border/50 pt-4 sm:scroll-mt-6" aria-labelledby="settings-hours">
        <h2 id="settings-hours" tabIndex={-1} className="text-xl font-[Cormorant_Garamond,serif] font-semibold outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
          Horários
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground">Defina o funcionamento normal e as exceções para datas especiais.</p>
      </div>

      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" />
            Horário de Funcionamento
          </CardTitle>
          <CardDescription className="text-xs">
            Defina os dias e horários em que o salão está aberto. Esses horários são exibidos na página pública e usados pelo agente de IA.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {scheduleLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {schedule.map((day, idx) => (
                  <div key={day.dayOfWeek} className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
                    day.isOpen ? "border-border/50 bg-background" : "border-border/30 bg-muted/30"
                  }`}>
                    {/* Toggle aberto/fechado */}
                    <Switch
                      checked={day.isOpen}
                      onCheckedChange={(checked) => {
                        const updated = [...schedule];
                        updated[idx] = { ...updated[idx]!, isOpen: checked };
                        setSchedule(updated);
                      }}
                    />
                    {/* Nome do dia */}
                    <span className={`w-36 text-sm font-medium ${
                      day.isOpen ? "text-foreground" : "text-muted-foreground"
                    }`}>
                      {DAY_NAMES_FULL[day.dayOfWeek]}
                    </span>
                    {/* Horários */}
                    {day.isOpen ? (
                      <div className="flex items-center gap-2 flex-1">
                        <div className="flex items-center gap-1.5">
                          <Label className="text-xs text-muted-foreground whitespace-nowrap">Abertura</Label>
                          <Input
                            type="time"
                            value={day.openTime}
                            onChange={(e) => {
                              const updated = [...schedule];
                              updated[idx] = { ...updated[idx]!, openTime: e.target.value };
                              setSchedule(updated);
                            }}
                            className="h-8 w-28 text-sm"
                          />
                        </div>
                        <span className="text-muted-foreground text-xs">às</span>
                        <div className="flex items-center gap-1.5">
                          <Label className="text-xs text-muted-foreground whitespace-nowrap">Fechamento</Label>
                          <Input
                            type="time"
                            value={day.closeTime}
                            onChange={(e) => {
                              const updated = [...schedule];
                              updated[idx] = { ...updated[idx]!, closeTime: e.target.value };
                              setSchedule(updated);
                            }}
                            className="h-8 w-28 text-sm"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 px-2 text-xs"
                          onClick={() => copyHoursToOpenDays(day)}
                          title="Aplicar este horário aos demais dias abertos"
                        >
                          <Copy className="w-3.5 h-3.5 mr-1" />
                          Copiar
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground italic flex-1">Fechado</span>
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-end pt-2">
                <Button
                  onClick={handleSaveSchedule}
                  disabled={setScheduleMutation.isPending}
                  size="sm"
                >
                  {setScheduleMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Salvar Horários
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

          {/* ─── Horários Especiais (Exceções) ────────────────────────────── */}
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarX className="w-4 h-4 text-primary" />
            Horários Especiais
          </CardTitle>
          <CardDescription className="text-xs">
            Cadastre feriados, recessos ou datas com horário diferente. O agente de IA respeitará automaticamente essas exceções.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Lista de exceções existentes */}
          {exceptionsData && exceptionsData.length > 0 ? (
            <div className="space-y-2">
              {exceptionsData.map((exc: any) => (
                <div
                  key={exc.id}
                  className="flex items-center justify-between rounded-lg border border-border/50 bg-secondary/10 px-3 py-2"
                >
                  <div className="flex items-center gap-3">
                    <CalendarClock className="w-4 h-4 text-muted-foreground shrink-0" />
                    <div>
                      <p className="text-sm font-medium">
                        {new Date(exc.exceptionDate + "T12:00:00").toLocaleDateString("pt-BR", {
                          weekday: "short",
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                        })}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {exc.isClosed
                          ? `Fechado${exc.reason ? ` - ${exc.reason}` : ""}`
                          : `Horario especial: ${exc.openTime ?? ""} - ${exc.closeTime ?? ""}${exc.reason ? ` - ${exc.reason}` : ""}`}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => deleteExceptionMutation.mutate({ id: exc.id })}
                    disabled={deleteExceptionMutation.isPending}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-3">
              Nenhuma exceção cadastrada. O salão seguirá o horário padrão todos os dias.
            </p>
          )}

          {/* Formulário para adicionar exceção */}
          {showExceptionForm ? (
            <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
              <p className="text-sm font-medium">Nova exceção de horário</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Data</Label>
                  <Input
                    type="date"
                    value={exceptionForm.exceptionDate}
                    onChange={(e) => setExceptionForm((f) => ({ ...f, exceptionDate: e.target.value }))}
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Motivo (opcional)</Label>
                  <Input
                    placeholder="Ex: Feriado Nacional, Recesso"
                    value={exceptionForm.reason}
                    onChange={(e) => setExceptionForm((f) => ({ ...f, reason: e.target.value }))}
                    className="h-8 text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={exceptionForm.isClosed}
                  onCheckedChange={(v) => setExceptionForm((f) => ({ ...f, isClosed: v }))}
                />
                <Label className="text-sm">
                  {exceptionForm.isClosed ? "Salão fechado neste dia" : "Horário diferente do padrão"}
                </Label>
              </div>
              {!exceptionForm.isClosed && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Abertura</Label>
                    <Input
                      type="time"
                      value={exceptionForm.openTime}
                      onChange={(e) => setExceptionForm((f) => ({ ...f, openTime: e.target.value }))}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Fechamento</Label>
                    <Input
                      type="time"
                      value={exceptionForm.closeTime}
                      onChange={(e) => setExceptionForm((f) => ({ ...f, closeTime: e.target.value }))}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              )}
              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  onClick={handleSaveException}
                  disabled={upsertExceptionMutation.isPending}
                  className="flex-1"
                >
                  {upsertExceptionMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Salvar Exceção
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowExceptionForm(false);
                    setExceptionForm({ exceptionDate: "", isClosed: true, openTime: "", closeTime: "", reason: "" });
                  }}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={() => setShowExceptionForm(true)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Exceção de Horário
            </Button>
          )}
        </CardContent>
      </Card>

      {/* ─── Configuração SMTP ──────────────────────────────────── */}
      <div className="scroll-mt-4 border-t border-border/50 pt-4 sm:scroll-mt-6" aria-labelledby="settings-advanced">
        <h2 id="settings-advanced" tabIndex={-1} className="text-xl font-[Cormorant_Garamond,serif] font-semibold outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
          Avançado
        </h2>
        <p className="mt-0.5 text-sm text-muted-foreground">Use estas configurações somente quando precisar de pagamentos, e-mail, contratos ou segurança.</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Mail className="w-4 h-4 text-primary" />
            Envio de E-mail (SMTP)
          </CardTitle>
          <CardDescription>
            Configure seu servidor de e-mail para enviar contratos e notificações automaticamente aos clientes.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="smtpHost">Servidor SMTP</Label>
              <Input
                id="smtpHost"
                placeholder="smtp.gmail.com"
                value={smtpForm.smtpHost}
                onChange={(e) => setSmtpForm((f) => ({ ...f, smtpHost: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Ex: smtp.gmail.com, smtp.office365.com</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtpPort">Porta</Label>
              <Input
                id="smtpPort"
                type="number"
                placeholder="587"
                value={smtpForm.smtpPort}
                onChange={(e) => setSmtpForm((f) => ({ ...f, smtpPort: parseInt(e.target.value) || 587 }))}
              />
              <p className="text-xs text-muted-foreground">587 (TLS) ou 465 (SSL)</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtpUser">Usuário / E-mail</Label>
              <Input
                id="smtpUser"
                type="email"
                placeholder="contato@meusalao.com"
                value={smtpForm.smtpUser}
                onChange={(e) => setSmtpForm((f) => ({ ...f, smtpUser: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="smtpPass">Senha / App Password</Label>
              <Input
                id="smtpPass"
                type="password"
                placeholder={smtpConfig?.smtpPassSet ? "••••••••••••• (salva)" : "Senha do e-mail"}
                value={smtpForm.smtpPass}
                onChange={(e) => setSmtpForm((f) => ({ ...f, smtpPass: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Para Gmail, use uma Senha de App (não a senha da conta).</p>
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label htmlFor="smtpFrom">Nome do Remetente (opcional)</Label>
              <Input
                id="smtpFrom"
                placeholder={`"${(salon as any)?.name || 'Meu Salão'}" <contato@meusalao.com>`}
                value={smtpForm.smtpFrom}
                onChange={(e) => setSmtpForm((f) => ({ ...f, smtpFrom: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Como o remetente aparecerá na caixa de entrada do cliente.</p>
            </div>
          </div>

          {/* Resultado do teste */}
          {smtpTestResult && (
            <div className={`flex items-center gap-2 p-3 rounded-md text-sm ${
              smtpTestResult.ok
                ? "bg-green-50 text-green-700 border border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800"
                : "bg-red-50 text-red-700 border border-red-200 dark:bg-red-950 dark:text-red-300 dark:border-red-800"
            }`}>
              {smtpTestResult.ok
                ? <CheckCircle className="w-4 h-4 flex-shrink-0" />
                : <XCircle className="w-4 h-4 flex-shrink-0" />}
              <span>
                {smtpTestResult.ok
                  ? "Conexão SMTP verificada com sucesso!"
                  : `Falha: ${smtpTestResult.error}`}
              </span>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={handleTestSmtp}
              disabled={testSmtpMutation.isPending}
            >
              {testSmtpMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Testar Conexão
            </Button>
            <Button
              size="sm"
              onClick={handleSaveSmtp}
              disabled={setSmtpConfigMutation.isPending}
            >
              {setSmtpConfigMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar Configuração
            </Button>
          </div>

          <div className="bg-muted/50 rounded-md p-3 text-xs text-muted-foreground space-y-1">
            <p className="font-medium text-foreground">Como configurar o Gmail:</p>
            <p>1. Ative a verificação em 2 etapas na sua conta Google.</p>
            <p>2. Acesse <strong>Segurança → Senhas de App</strong> e crie uma senha para "Correio".</p>
            <p>3. Use <strong>smtp.gmail.com</strong>, porta <strong>587</strong> e a senha de app gerada.</p>
          </div>
        </CardContent>
      </Card>
      {/* ─── Pagamento Antecipado ─────────────────────────────────────────────────────────────── */}
      <Card className="border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CreditCard className="w-4 h-4 text-primary" />
            Pagamento Antecipado
          </CardTitle>
          <CardDescription className="text-xs">
            Defina o sinal e as regras exibidas no agente de IA e na reserva pública. Em reservas online, o pagamento pelo Stripe confirma o horário automaticamente.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Exigir pagamento antecipado</p>
              <p className="text-xs text-muted-foreground">O agente solicitará PIX ou link antes de confirmar o horário</p>
            </div>
            <Switch
              checked={paymentForm.requireAdvancePayment}
              onCheckedChange={(v) => setPaymentForm((f) => ({ ...f, requireAdvancePayment: v }))}
            />
          </div>
          {paymentForm.requireAdvancePayment && (
            <div className="space-y-4 pt-2 border-t border-border/50">
              <div className="space-y-2">
                <Label className="text-xs">Percentual a cobrar</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min={10}
                    max={100}
                    step={10}
                    value={paymentForm.advancePaymentPercent}
                    onChange={(e) => setPaymentForm((f) => ({ ...f, advancePaymentPercent: Number(e.target.value) }))}
                    className="flex-1"
                  />
                  <Badge variant="outline" className="min-w-[50px] justify-center">
                    {paymentForm.advancePaymentPercent}%
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  {paymentForm.advancePaymentPercent === 100 ? "Cobrar 100% do valor (pagamento total antecipado)" : `Cobrar ${paymentForm.advancePaymentPercent}% como sinal (entrada)`}
                </p>
              </div>
              <div className="grid gap-4 rounded-lg border border-border/50 bg-muted/20 p-3 sm:grid-cols-[180px_1fr]">
                <div className="space-y-2">
                  <Label className="text-xs">Antecedência para cancelamento</Label>
                  <Input
                    type="number"
                    min={0}
                    max={720}
                    value={paymentForm.publicBookingCancellationHours}
                    onChange={(e) => setPaymentForm((f) => ({ ...f, publicBookingCancellationHours: Math.max(0, Math.min(720, Number(e.target.value) || 0)) }))}
                    className="h-9 text-xs"
                  />
                  <p className="text-xs text-muted-foreground">Em horas. Use 0 para avaliação caso a caso.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Política exibida à cliente</Label>
                  <Textarea
                    value={paymentForm.publicBookingCancellationPolicy}
                    onChange={(e) => setPaymentForm((f) => ({ ...f, publicBookingCancellationPolicy: e.target.value }))}
                    placeholder="Ex.: O sinal não é reembolsável em cancelamentos com menos de 24 horas."
                    className="min-h-20 resize-y text-xs"
                    maxLength={1200}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-1"><Banknote className="w-3.5 h-3.5" /> Chave PIX</Label>
                <div className="flex gap-2">
                  <Select
                    value={paymentForm.pixKeyType}
                    onValueChange={(v) => setPaymentForm((f) => ({ ...f, pixKeyType: v as any }))}
                  >
                    <SelectTrigger className="w-36 h-9 text-xs">
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cpf">CPF</SelectItem>
                      <SelectItem value="cnpj">CNPJ</SelectItem>
                      <SelectItem value="email">E-mail</SelectItem>
                      <SelectItem value="telefone">Telefone</SelectItem>
                      <SelectItem value="aleatoria">Aleatória</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    className="h-9 text-xs flex-1"
                    placeholder="Ex: 11999999999 ou email@exemplo.com"
                    value={paymentForm.pixKey}
                    onChange={(e) => setPaymentForm((f) => ({ ...f, pixKey: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-1"><Link2 className="w-3.5 h-3.5" /> Link de Pagamento (opcional)</Label>
                <Input
                  className="h-9 text-xs"
                  placeholder="Ex: https://nubank.com.br/cobrar/... ou link do Mercado Pago"
                  value={paymentForm.paymentLink}
                  onChange={(e) => setPaymentForm((f) => ({ ...f, paymentLink: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">Nubank, Mercado Pago, PagBank, etc. Opcional para atendimento pelo agente; a reserva pública usa o checkout Stripe seguro.</p>
              </div>
              <div className="bg-amber-50/10 border border-amber-500/20 rounded-md p-3 text-xs text-muted-foreground">
                <p className="font-medium text-amber-600 dark:text-amber-400 mb-1">Como funciona:</p>
                <ol className="space-y-1 list-decimal list-inside">
                  <li>Cliente escolhe serviço, profissional e horário com o agente</li>
                  <li>Agente informa o valor e envia a chave PIX ou link</li>
                  <li>Cliente realiza o pagamento e envia o comprovante</li>
                  <li>Você aprova em <strong>Comprovantes Pendentes</strong> no menu</li>
                  <li>Agendamento é confirmado automaticamente</li>
                </ol>
              </div>
            </div>
          )}
          <div className="space-y-3 rounded-lg border border-blue-200 bg-blue-50/40 p-3">
            <div>
              <p className="text-sm font-medium text-blue-950">Taxas de cartão na comissão</p>
              <p className="text-xs text-muted-foreground">Preencha somente as taxas que devem reduzir a base da comissão. Campo em branco mantém a comissão sobre o valor integral.</p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label className="text-xs">Débito (%)</Label>
                <Input type="number" min="0" max="100" step="0.01" placeholder="Ex.: 1,99" value={paymentForm.cardDebitFeePercent} onChange={(e) => setPaymentForm((form) => ({ ...form, cardDebitFeePercent: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Crédito à vista — 1x (%)</Label>
                <Input type="number" min="0" max="100" step="0.01" placeholder="Ex.: 2,99" value={paymentForm.cardCreditFeePercent} onChange={(e) => setPaymentForm((form) => ({ ...form, cardCreditFeePercent: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Crédito parcelado (%)</Label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-6">
                {Array.from({ length: 11 }, (_, index) => index + 2).map((installments) => (
                  <div key={installments} className="space-y-1">
                    <Label className="text-xs text-muted-foreground">{installments}x</Label>
                    <Input type="number" min="0" max="100" step="0.01" placeholder="—" value={paymentForm.cardCreditInstallmentFeePercents[String(installments)] ?? ""} onChange={(e) => setPaymentForm((form) => ({ ...form, cardCreditInstallmentFeePercents: { ...form.cardCreditInstallmentFeePercents, [String(installments)]: e.target.value } }))} />
                  </div>
                ))}
              </div>
            </div>
          </div>
          <Button
            size="sm"
            onClick={handleSavePaymentConfig}
            disabled={updatePaymentConfigMutation.isPending}
          >
            {updatePaymentConfigMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Salvar Configuração de Pagamento
          </Button>
        </CardContent>
      </Card>
      {/* ─── Contrato de Prestação de Serviços ────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Shield className="w-4 h-4 text-primary" />
            Contrato de Prestação de Serviços
          </CardTitle>
          <CardDescription>
            Documento oficial que protege seu negócio legalmente. Disponível para seus clientes baixarem.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-secondary/20 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium text-foreground">O contrato inclui:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {[
                "Política de cancelamento (24h)",
                "Taxa de no-show (50%)",
                "Responsabilidades do salão e do cliente",
                "Garantia de 7 dias em serviços químicos",
                "Autorização de uso de imagem",
                "Proteção de dados (LGPD)",
                "Resolução de conflitos (CDC)",
                "Conduta no estabelecimento",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`/contrato${salon?.id ? `?salonId=${salon.id}` : ""}`, "_blank")}
              className="flex-1"
            >
              <FileText className="w-4 h-4 mr-2" />
              Visualizar Contrato
            </Button>
            <Button
              size="sm"
              onClick={() => {
                const url = `/api/contract/download${salon?.id ? `?salonId=${salon.id}` : ""}`;
                const link = document.createElement("a");
                link.href = url;
                link.download = "contrato-prestacao-servicos.pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              className="flex-1 bg-primary text-primary-foreground"
            >
              <Download className="w-4 h-4 mr-2" />
              Baixar PDF do Contrato
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Compartilhe o link <code className="bg-secondary px-1 rounded text-xs">/contrato</code> com seus clientes
            ou adicione-o na confirmação de agendamento para que eles possam ler e baixar.
          </p>
        </CardContent>
      </Card>

      {/* ─── Contrato B2B SaaS — Beleza e Bronze ───────────────────────────── */}
      <Card className="border-primary/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="w-4 h-4 text-primary" />
            Contrato de Licença de Uso da Plataforma (B2B)
          </CardTitle>
          <CardDescription>
            Contrato entre a <strong>Beleza e Bronze</strong> (CNPJ 40.619.126/0001-63) e os salões que contratam a plataforma.
            Protege sua empresa como fornecedora de software.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-secondary/20 rounded-lg p-4 space-y-2">
            <p className="text-sm font-medium text-foreground">O contrato B2B inclui:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-1.5">
              {[
                "Licença de uso da plataforma",
                "Limitação de responsabilidade técnica",
                "Política de pagamento e cancelamento",
                "SLA e disponibilidade do sistema",
                "Propriedade intelectual (código e marca)",
                "LGPD: Controlador vs. Operador",
                "Rescisão e portabilidade de dados",
                "Proibição de engenharia reversa",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <CheckCircle2 className="w-3.5 h-3.5 text-green-500 shrink-0" />
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open("/contrato-saas", "_blank")}
              className="flex-1"
            >
              <FileText className="w-4 h-4 mr-2" />
              Visualizar Contrato B2B
            </Button>
            <Button
              size="sm"
              onClick={() => {
                const url = "/api/contract/saas/download";
                const link = document.createElement("a");
                link.href = url;
                link.download = "contrato-saas-beleza-e-bronze.pdf";
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }}
              className="flex-1 bg-primary text-primary-foreground"
            >
              <Download className="w-4 h-4 mr-2" />
              Baixar PDF do Contrato B2B
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Envie este contrato para novos salões antes de ativá-los na plataforma.
            Acesse também em{" "}
            <code className="bg-secondary px-1 rounded text-xs">/contrato-saas</code>.
          </p>
        </CardContent>
      </Card>

      {/* ─── Customização do Site Público ─────────────────────────────────── */}
      <div className="border-t border-border/50 pt-4" aria-labelledby="settings-public-store">
        <h3 id="settings-public-store" className="text-lg font-[Cormorant_Garamond,serif] font-semibold">Loja e site público</h3>
        <p className="mt-0.5 text-sm text-muted-foreground">Personalize a vitrine, os links e a aparência que seus clientes veem.</p>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Palette className="h-4 w-4 text-primary" />
            Customização do Site Público
          </CardTitle>
          <CardDescription>Personalize a aparência do seu site e loja para os clientes.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">

          {/* Banner Hero */}
          <div>
            <Label className="text-sm font-medium mb-2 block">Banner Hero (imagem principal)</Label>
            <div className="relative w-full h-40 rounded-lg overflow-hidden border-2 border-dashed border-border bg-muted flex items-center justify-center">
              {bannerPreview ? (
                <img src={bannerPreview} alt="Preview banner" className="w-full h-full object-cover" />
              ) : (salon as any)?.bannerUrl ? (
                <img src={(salon as any).bannerUrl} alt="Banner atual" className="w-full h-full object-cover" />
              ) : (
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                  <ImageIcon className="h-8 w-8" />
                  <span className="text-sm">Nenhum banner definido</span>
                </div>
              )}
              {(bannerPreview || (salon as any)?.bannerUrl) && (
                <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                  <span className="text-white text-sm">Clique abaixo para trocar</span>
                </div>
              )}
            </div>
            <div className="flex gap-2 mt-2">
              <label className="flex-1">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 5 * 1024 * 1024) { toast.error("Imagem deve ter no máximo 5MB."); return; }
                    const reader = new FileReader();
                    reader.onload = (ev) => setBannerPreview(ev.target?.result as string);
                    reader.readAsDataURL(file);
                  }}
                />
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <span className="cursor-pointer"><Upload className="h-3 w-3 mr-1" />Selecionar imagem</span>
                </Button>
              </label>
              {bannerPreview && (
                <Button variant="ghost" size="sm" onClick={() => setBannerPreview(null)}>
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            <div className="mt-2 rounded-lg border border-primary/20 bg-primary/5 p-2.5 text-xs text-muted-foreground">
              <p className="font-semibold text-foreground">Formato recomendado para o banner</p>
              <p className="mt-1">
                Use uma imagem <strong>horizontal 16:5</strong> — recomendado <strong>1920 × 600 px</strong>, em JPG, PNG ou WebP, com no máximo 5 MB.
              </p>
              <p className="mt-1">
                Deixe logos, rostos e textos importantes no centro da imagem: o banner se adapta à tela e pode cortar as laterais no celular.
              </p>
            </div>
          </div>

          {/* Textos do Hero */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Type className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Textos do Hero</span>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Slogan principal</Label>
              <Input
                value={siteForm.heroSlogan}
                onChange={(e) => setSiteForm({ ...siteForm, heroSlogan: e.target.value })}
                placeholder="Ex: Realce sua beleza natural"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Subtítulo</Label>
              <Input
                value={siteForm.heroSubtitle}
                onChange={(e) => setSiteForm({ ...siteForm, heroSubtitle: e.target.value })}
                placeholder="Ex: Especialistas em bronzeamento e estética"
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Texto do botão CTA</Label>
              <Input
                value={siteForm.ctaButtonText}
                onChange={(e) => setSiteForm({ ...siteForm, ctaButtonText: e.target.value })}
                placeholder="Ex: Agendar agora"
                className="mt-1"
              />
            </div>
          </div>

          {/* Botão exclusivo da loja */}
          <div className="space-y-3 rounded-xl border border-border/60 bg-muted/20 p-4">
            <div className="flex items-center gap-2">
              <Store className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Botão da Loja</span>
            </div>
            <p className="text-xs text-muted-foreground">
              Personalize o atalho exibido no topo da loja. Ele pode abrir o WhatsApp ou outro link seguro.
            </p>
            <div>
              <Label className="text-xs text-muted-foreground">Nome do botão</Label>
              <Input
                value={siteForm.storeCtaButtonText}
                onChange={(e) => setSiteForm({ ...siteForm, storeCtaButtonText: e.target.value })}
                placeholder="Ex: Peça já"
                maxLength={100}
                className="mt-1"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Para onde o botão deve levar</Label>
              <Input
                type="url"
                value={siteForm.storeCtaButtonUrl}
                onChange={(e) => setSiteForm({ ...siteForm, storeCtaButtonUrl: e.target.value })}
                placeholder="https://wa.me/5511999999999"
                maxLength={500}
                className="mt-1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Use um link completo. Se deixar em branco, a loja usa o WhatsApp configurado; sem WhatsApp, abre o agendamento.
              </p>
            </div>
          </div>

          {/* Domínio próprio da loja */}
          <div className="space-y-3 rounded-xl border border-primary/25 bg-primary/5 p-4">
            <div className="flex flex-wrap items-center gap-2">
              <Link2 className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium">Domínio próprio da loja</span>
              {publicStoreDomainStatus === "verified" ? (
                <Badge className="bg-emerald-600 text-white">Verificado</Badge>
              ) : publicStoreDomainStatus === "pending_dns" ? (
                <Badge variant="outline" className="border-amber-400 text-amber-700">Aguardando DNS</Badge>
              ) : (
                <Badge variant="outline" className="text-muted-foreground">Não configurado</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Use o endereço próprio do salão na vitrine e no QR Code. Cada domínio pode pertencer a apenas uma unidade.
            </p>
            <div>
              <Label className="text-xs text-muted-foreground">Endereço da loja</Label>
              <Input
                value={siteForm.publicStoreDomain}
                onChange={(e) => setSiteForm({ ...siteForm, publicStoreDomain: e.target.value })}
                placeholder="loja.seusalao.com.br"
                maxLength={255}
                autoCapitalize="none"
                autoCorrect="off"
                className="mt-1"
              />
              <p className="mt-1 text-xs text-muted-foreground">
                Informe somente o domínio, sem <code>https://</code>, barra, porta ou caminho. Para remover, apague o campo e salve.
              </p>
            </div>

            {publicStoreDomainStatus === "pending_dns" && publicStoreDomain && publicStoreVerificationHost && publicStoreVerificationValue && (
              <div className="space-y-3 rounded-lg border border-amber-300/60 bg-amber-50/60 p-3 text-sm dark:bg-amber-950/15">
                <p className="font-medium text-amber-950 dark:text-amber-100">1. Comprove que o domínio é seu</p>
                <p className="text-xs text-muted-foreground">
                  No provedor DNS do domínio, crie este registro <strong>TXT</strong>. Depois de salvar, a propagação pode levar alguns minutos.
                </p>
                <div className="grid gap-2 text-xs">
                  <div className="rounded-md border bg-background p-2">
                    <span className="block text-muted-foreground">Nome / Host</span>
                    <div className="mt-1 flex items-center gap-2">
                      <code className="min-w-0 flex-1 break-all text-foreground">{publicStoreVerificationHost}</code>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => { navigator.clipboard.writeText(publicStoreVerificationHost); toast.success("Nome do TXT copiado!"); }} aria-label="Copiar nome do registro TXT"><Copy className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                  <div className="rounded-md border bg-background p-2">
                    <span className="block text-muted-foreground">Valor</span>
                    <div className="mt-1 flex items-center gap-2">
                      <code className="min-w-0 flex-1 break-all text-foreground">{publicStoreVerificationValue}</code>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => { navigator.clipboard.writeText(publicStoreVerificationValue); toast.success("Código de verificação copiado!"); }} aria-label="Copiar valor do registro TXT"><Copy className="h-3.5 w-3.5" /></Button>
                    </div>
                  </div>
                </div>
                <Button type="button" size="sm" variant="outline" className="gap-1.5" onClick={() => verifyStoreCustomDomainMutation.mutate()} disabled={verifyStoreCustomDomainMutation.isPending}>
                  {verifyStoreCustomDomainMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  Verificar DNS
                </Button>
              </div>
            )}

            {publicStoreDomainStatus === "verified" && publicStoreDomain && (
              <div className="space-y-3 rounded-lg border border-emerald-300/60 bg-emerald-50/70 p-3 text-xs text-emerald-950 dark:bg-emerald-950/15 dark:text-emerald-100">
                <div>
                  <p className="font-medium">Domínio comprovado: {publicStoreDomain}</p>
                  <p className="mt-1 text-emerald-800 dark:text-emerald-200">
                    O link e o QR Code já apontam para esse endereço. A comprovação por TXT confirma que a unidade controla o domínio, mas ainda não conecta a loja à hospedagem.
                  </p>
                </div>
                <div className="rounded-md border border-emerald-300/50 bg-white/60 p-3 text-emerald-950 dark:bg-emerald-950/20 dark:text-emerald-100">
                  <p className="font-semibold">Próxima etapa: ativar na hospedagem</p>
                  <ol className="mt-2 list-decimal space-y-1 pl-4 text-emerald-800 dark:text-emerald-200">
                    <li>Adicione <strong>{publicStoreDomain}</strong> como domínio personalizado deste projeto na plataforma de hospedagem.</li>
                    <li>Copie os registros <strong>A</strong> ou <strong>CNAME</strong> exibidos pela hospedagem e crie-os no provedor DNS do domínio.</li>
                    <li>Aguarde a propagação e abra <strong>https://{publicStoreDomain}</strong> antes de divulgar o novo link ou QR Code.</li>
                  </ol>
                  <p className="mt-2 text-emerald-800 dark:text-emerald-200">
                    Use somente os valores gerados pela hospedagem deste projeto. A conexão de DNS não é feita automaticamente pelo painel do salão.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Cores do Tema */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Palette className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Cores do Tema</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Cor de destaque</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={siteForm.themeColor}
                    onChange={(e) => setSiteForm({ ...siteForm, themeColor: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border border-border"
                  />
                  <Input
                    value={siteForm.themeColor}
                    onChange={(e) => setSiteForm({ ...siteForm, themeColor: e.target.value })}
                    placeholder="#C97D2E"
                    className="flex-1 font-mono text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Cor de fundo</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={siteForm.themeBgColor}
                    onChange={(e) => setSiteForm({ ...siteForm, themeBgColor: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border border-border"
                  />
                  <Input
                    value={siteForm.themeBgColor}
                    onChange={(e) => setSiteForm({ ...siteForm, themeBgColor: e.target.value })}
                    placeholder="#F5F0E8"
                    className="flex-1 font-mono text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Cor escura (texto/botões)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={siteForm.themeDarkColor}
                    onChange={(e) => setSiteForm({ ...siteForm, themeDarkColor: e.target.value })}
                    className="w-10 h-10 rounded cursor-pointer border border-border"
                  />
                  <Input
                    value={siteForm.themeDarkColor}
                    onChange={(e) => setSiteForm({ ...siteForm, themeDarkColor: e.target.value })}
                    placeholder="#2C1810"
                    className="flex-1 font-mono text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Redes Sociais */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Share2 className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Redes Sociais</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Instagram</Label>
                <Input
                  value={siteForm.instagramUrl}
                  onChange={(e) => setSiteForm({ ...siteForm, instagramUrl: e.target.value })}
                  placeholder="https://instagram.com/seusalao"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Facebook</Label>
                <Input
                  value={siteForm.facebookUrl}
                  onChange={(e) => setSiteForm({ ...siteForm, facebookUrl: e.target.value })}
                  placeholder="https://facebook.com/seusalao"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">TikTok</Label>
                <Input
                  value={siteForm.tiktokUrl}
                  onChange={(e) => setSiteForm({ ...siteForm, tiktokUrl: e.target.value })}
                  placeholder="https://tiktok.com/@seusalao"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">WhatsApp (link direto)</Label>
                <Input
                  value={siteForm.whatsappUrl}
                  onChange={(e) => setSiteForm({ ...siteForm, whatsappUrl: e.target.value })}
                  placeholder="https://wa.me/5511999999999"
                  className="mt-1"
                />
              </div>
            </div>
          </div>

          {/* Visibilidade de Seções */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Eye className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Visibilidade de Seções</span>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Seção da Equipe</p>
                  <p className="text-xs text-muted-foreground">Exibe os profissionais do salão</p>
                </div>
                <Switch
                  checked={siteForm.showTeamSection}
                  onCheckedChange={(v) => setSiteForm({ ...siteForm, showTeamSection: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Seção de Avaliações</p>
                  <p className="text-xs text-muted-foreground">Exibe avaliações dos clientes</p>
                </div>
                <Switch
                  checked={siteForm.showReviewsSection}
                  onCheckedChange={(v) => setSiteForm({ ...siteForm, showReviewsSection: v })}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Seção de Horários</p>
                  <p className="text-xs text-muted-foreground">Exibe os horários de funcionamento</p>
                </div>
                <Switch
                  checked={siteForm.showHoursSection}
                  onCheckedChange={(v) => setSiteForm({ ...siteForm, showHoursSection: v })}
                />
              </div>
            </div>
          </div>

          <Button
            onClick={handleSaveSite}
            disabled={updateMutation.isPending || bannerUploading}
            className="w-full sm:w-auto"
          >
            {(updateMutation.isPending || bannerUploading) ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Salvando...</>
            ) : (
              <><Save className="h-4 w-4 mr-2" />Salvar Customizações</>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* ─── Seção de Segurança ─────────────────────────────────────────────── */}
      <SecuritySection />
    </div>
  );
}

// ─── Componente de Alteração de Senha ────────────────────────────────────────
function SecuritySection() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const setPasswordMutation = (trpc as any).auth.setPassword.useMutation({
    onSuccess: () => {
      toast.success("Senha alterada com sucesso!");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("A nova senha deve ter pelo menos 6 caracteres");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    setPasswordMutation.mutate({
      currentPassword: currentPassword || undefined,
      newPassword,
    });
  };

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Shield className="w-4 h-4 text-amber-500" />
          Segurança — Alterar Senha
        </CardTitle>
        <CardDescription>
          Atualize a senha de acesso ao painel administrativo.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleChangePassword} className="space-y-4 max-w-sm">
          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Senha atual</Label>
            <div className="relative">
              <Input
                type={showCurrent ? "text" : "password"}
                placeholder="Senha atual (deixe em branco se nunca definiu)"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="pr-10"
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showCurrent ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Nova senha</Label>
            <div className="relative">
              <Input
                type={showNew ? "text" : "password"}
                placeholder="Mínimo 6 caracteres"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="pr-10"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showNew ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium">Confirmar nova senha</Label>
            <div className="relative">
              <Input
                type={showConfirm ? "text" : "password"}
                placeholder="Repita a nova senha"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pr-10"
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showConfirm ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                )}
              </button>
            </div>
          </div>

          <Button
            type="submit"
            size="sm"
            disabled={setPasswordMutation.isPending || !newPassword}
            className="gap-2"
          >
            {setPasswordMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Shield className="w-4 h-4" />
            )}
            Salvar nova senha
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
